import { IDescription, IName, INewName, IAddressFormat, INewDescription } from '../interfaces/global.interfaces';
import { IaOLO } from '../interfaces/aolo.interface';
import { IDataCountry, IDataHoliday, IDataOrderTypeSubType, IDataStore, IDeliveryZonePolygon, IStoreLocationZone, IStoreLocationZonePolygon } from '../models/data.interface';
import { IFormattedAddress, IDateStartEndTime } from './util.interface';
import { Data } from '../models/data';
import { LocalizationService } from '../services/localization-service';
import { AppPlatform, BahamasCoordinates, CardBrand, CookiePreferences } from '../types/enums';
import { ITempAddress } from '../interfaces/temp.interface';
import { IUserAddress } from '../models/user.interface';
import { IOrderPayment } from '../models/order.interface';
import { IAddressZone } from '../online-ordering/interfaces/online-ordering.interface';
import { awaitNativeOnce } from "../utils/native-bridge";
import type { SimpleCoords } from "../types/geolocation-types";

export class Util {
    private static useFixedTime: boolean = false;
    private static fixedTime: string = "";
    private static _coordsInFlight: Promise<SimpleCoords | null> | null = null;

    private static readonly creditCardTypes: Record<number, string> = {
        1: "Visa",
        2: "MasterCard",
        3: "Discover",
        4: "Amex",
        5: "JCB",
        6: "DinersClub",
        7: "enRoute",
        8: "Interac",
        9: "Flash",
        10: "Debit",
        11: "GiftCard",
        12: "EBT",
        13: "LevelUp",
        14: "Voyager",
        15: "Wex",
        16: "Other",
    };

    /*******************************************/
    /********* Format String *******************/
    /*******************************************/
    /**
    * Formats a number as a currency string in USD.
    * 
    * @param {number} amount - The amount to format.
    * @returns {string} - The formatted currency string.
    */
    public static formatMoney(amount: number, country: IDataCountry): string {
        const formatter = new Intl.NumberFormat(country.DefaultCultureCode || 'en-US', {
            style: 'currency',
            currency: country.CurrencyCode || 'USD',
            currencyDisplay: 'narrowSymbol',
            minimumFractionDigits: 2
        });

        return formatter.format(Number(amount.toFixed(2)));
    }

    /**
    * Formats a number with two decimal places.
    * 
    * @param {number} amount - The number to format.
    * @returns {string} - The formatted number string.
    */
    public static formatNumber(amount: number): string {
        const formatter = new Intl.NumberFormat('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        return formatter.format(Number(amount.toFixed(2)));
    }

    public static cleanNumber(amount: string, country: IDataCountry): string {
        const culture = country.DefaultCultureCode ?? 'en-US';

        // Culture-specific separators
        const decimalSeparator = new Intl.NumberFormat(culture)
            .format(1.1)
            .replace(/\d/g, ""); // get separator between 1 and 1
        const groupSeparator = new Intl.NumberFormat(culture)
            .format(1000)
            .replace(/\d/g, ""); // get separator in 1,000

        // Normalize number string
        const normalized = amount
            .split(groupSeparator).join("")       // remove thousand separators
            .replace(decimalSeparator, ".");      // unify decimal to "."

        return normalized;
    }

    /**
    * Formats a Date object into a localized date string.
    * 
    * @param {Date} date - The date to format.
    * @returns {string} - The formatted date string.
    */
    public static formatDate(date: Date, language: string | null = null): string {
        if (language == null)
            language = Util.GetBrowserLanguage();
        const options = {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric'
        } as const;
        const newDate = new Date(date);
        const formatter = new Intl.DateTimeFormat(language, options);
        return formatter.format(newDate);
    }

    /**
    * Formats a Date object into a date string suitable for input fields (YYYY-MM-DD).
    * 
    * @param {Date} date - The date to format.
    * @returns {string} - The formatted date string for input fields.
    */
    public static formatDateInput(date: Date): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const day = date.getDate().toString().padStart(2, '0');

        return `${year}-${month}-${day}`;
    }

    /**
    * Transforms a date string in the format "YYYY-MM-DD" to "MM/DD/YYYY".
    * 
    * @param {string} date - A string representation of a date in the format "YYYY-MM-DD".
    * @returns {string} The input date string in the format "MM/DD/YYYY".
    */
    public static formatDateFunc(date: string): string {
        const datePattern = /^\d{4}-\d{2}-\d{2}$/;
        if (!datePattern.test(date))
            throw new Error('Invalid date format. Expected format: YYYY-MM-DD');

        const [year, month, day] = date.split("-");

        return `${month}/${day}/${year}`;
    }

    /**
    * Formats a Date object into a localized date and time string.
    * 
    * @param {Date} date - The date to format.
    * @returns {string} - The formatted date and time string.
    */
    public static formatDateTime(date: Date, language: string | null = null): string {
        if (language == null)
            language = Util.GetBrowserLanguage();

        const options = {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        } as const;

        const newDate = new Date(date);
        const formatter = new Intl.DateTimeFormat(language, options);
        return formatter.format(newDate);
    }

    /**
    * Formats a Date object into a localized date and time string, including seconds.
    * 
    * @param {Date} date - The date to format.
    * @returns {string} - The formatted date and time string, including seconds.
    */
    public static formatDateTimeSeconds(date: Date): string {
        if (isNaN(new Date(date).getTime()))
            throw new Error('Invalid time value');

        const newDate = new Date(date);
        const formattedDate = newDate.toLocaleDateString('en-US', {
            month: '2-digit',
            day: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: true,
        }).replace(",", "");
        return formattedDate;
    }

    /**
     * Formats a given date into a full human-readable string based on the provided language.
     * The format includes the weekday, month, day, hour, and minute.
     *
     * @param {Date | string} date - The date to be formatted. Can be a `Date` object or an ISO string.
     * @param {string | null} [language=null] - The language to use for formatting (e.g., "en-US", "es-MX").
     * @returns {string} - The formatted date string (e.g., "Tuesday, August 6 at 6:00 PM").
     */
    public static formatDateTimeFull(date: Date | string, language: string | null = null): string {
        if (language == null)
            language = Util.GetBrowserLanguage();

        const options = {
            weekday: "long",  // Full name of the day (e.g., Tuesday)
            month: "long",    // Full name of the month (e.g., August)
            day: "numeric",   // Day of the month (e.g., 6)
            hour: "numeric",  // Numeric hour
            minute: "2-digit" // Two-digit minute
        } as const;

        const newDate = new Date(date);
        const formatter = new Intl.DateTimeFormat(language, options);
        return formatter.format(newDate);
    }

    /**
    * Formats a Date object into a localized time string.
    * 
    * @param {Date} date - The date to format.
    * @returns {string} - The formatted time string.
    */
    public static formatTime(date: Date, isoCode: string | undefined | null = null): string {
        if (isNaN(new Date(date).getTime()))
            throw new Error('Invalid time value');

        const now = new Date(date);
        const timeString = now.toLocaleTimeString(isoCode ? isoCode : [], { hour: '2-digit', minute: '2-digit', hour12: true });
        return timeString;
    }

    public static formatTimeByIsoCode(minutes: number, cultureCode: string): string {
        if (cultureCode == "en-us")
            cultureCode = "en-US";
        else if (cultureCode == 'sp-mx')
            cultureCode = "es-MX";

        // Calculate total minutes within a 24-hour period
        const normalizedMinutes = minutes % 1440;

        // Get the hour and minute components
        const hours = Math.floor(normalizedMinutes / 60);
        const mins = normalizedMinutes % 60;

        // Determine 12-hour or 24-hour format using Intl.DateTimeFormat
        const hourCycle = new Intl.DateTimeFormat(cultureCode, { hour: 'numeric' })
            .resolvedOptions().hourCycle;

        // Determine if the culture prefers a 12-hour format
        const is12HourFormat = hourCycle === 'h12' || hourCycle === 'h11';

        if (is12HourFormat) {
            // 12-hour format
            const period = hours >= 12 ? 'PM' : 'AM';
            const hour12 = hours % 12 === 0 ? 12 : hours % 12;
            return `${hour12}:${mins.toString().padStart(2, '0')} ${period}`;
        } else {
            // 24-hour format
            return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
        }
    }

    /**
    * Adds a specific amount of time units to a given Date object.
    * 
    * @param {Date} date - The original date.
    * @param {number|string} value - The amount to add.
    * @param {string} addingUnit - The unit of time to add (e.g., 'd' for days, 'M' for months).
    * @returns {Date} - The new date after addition.
    */
    public static DateAdd(date: Date, value: number | string, addingUnit: string): Date {
        const iValue = (typeof value == "string") ? parseInt(value) : value;
        const iDate = new Date(date);
        switch (addingUnit) {
            case "ms":
                return new Date(iDate.setTime(iDate.getTime() + iValue));
            case "s":
                return new Date(iDate.setSeconds(iDate.getSeconds() + iValue));
            case "m":
                return new Date(iDate.setMinutes(iDate.getMinutes() + iValue));
            case "h":
                return new Date(iDate.setHours(iDate.getHours() + iValue));
            case "d":
                return new Date(iDate.setDate(iDate.getDate() + iValue));
            case "M":
                return new Date(iDate.setMonth(iDate.getMonth() + iValue));
        }
        return iDate;
    }

    public static DateDiff(diffIn: string, date1: Date, date2: Date): number {
        let millisecondsPerDiff = 1000 * 60 * 60 * 24;
        switch (diffIn) {
            case "ms":
                millisecondsPerDiff = 1;
                break;
            case "s":
                millisecondsPerDiff = 1000;
                break;
            case "m":
                millisecondsPerDiff = 1000 * 60;
                break;
            case "h":
                millisecondsPerDiff = 1000 * 60 * 60;
                break;
            case "d":
                date1 = new Date(Util.formatDate(date1, 'en-US'));
                date2 = new Date(Util.formatDate(date2, 'en-US'));
                millisecondsPerDiff = 1000 * 60 * 60 * 24;
                break;
            case "M":
                return date1.getFullYear() * 12 + date1.getMonth() - (date2.getFullYear() * 12 + date2.getMonth());
        }

        const date1_ms = date1.getTime();
        const date2_ms = date2.getTime();
        const difference_ms = date1_ms - date2_ms;
        return Math.round(difference_ms / millisecondsPerDiff);
    }

    /*******************************************/
    /**************** Encryption ***************/
    /*******************************************/
    public static async encrypt(data: string, publicKey: string) {
        const publicKeyBuffer = this.base64ToBytes(publicKey);

        const cryptoKey = await crypto.subtle.importKey(
            "spki",
            publicKeyBuffer,
            {
                name: "RSA-OAEP",
                hash: { name: "SHA-256" }
            },
            false,
            ["encrypt"]
        );

        // Generate symmetric key and encrypt data
        const symmetricKey = await this.generateSymmetricKey();
        const encryptedData = await this.encryptWithSymmetricKey(symmetricKey, data);

        // Export the symmetric key as raw bytes
        const symmetricKeyBytes = await crypto.subtle.exportKey("raw", symmetricKey);

        // Encrypt the symmetric key using the RSA public key
        const encryptedSymmetricKey: ArrayBuffer = await crypto.subtle.encrypt(
            {
                name: "RSA-OAEP"
            },
            cryptoKey,
            symmetricKeyBytes
        );

        // Bundle the encrypted symmetric key, the IV, and the encrypted data
        const result = {
            encryptedSymmetricKey: this.bytesToBase64(new Uint8Array(encryptedSymmetricKey)),
            iv: this.bytesToBase64(encryptedData.iv),
            encryptedData: this.bytesToBase64(new Uint8Array(encryptedData.encryptedData)),
            tag: this.bytesToBase64(new Uint8Array(encryptedData.tag))
        };

        return result;
    }

    public static getBase64Code(charCode: number) {
        const base64codes = [
            255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255,
            255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255,
            255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 255, 62, 255, 255, 255, 63,
            52, 53, 54, 55, 56, 57, 58, 59, 60, 61, 255, 255, 255, 0, 255, 255,
            255, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14,
            15, 16, 17, 18, 19, 20, 21, 22, 23, 24, 25, 255, 255, 255, 255, 255,
            255, 26, 27, 28, 29, 30, 31, 32, 33, 34, 35, 36, 37, 38, 39, 40,
            41, 42, 43, 44, 45, 46, 47, 48, 49, 50, 51
        ];

        if (charCode >= base64codes.length) {
            throw new Error("Unable to parse base64 string.");
        }
        const code = base64codes[charCode];
        if (code === 255) {
            throw new Error("Unable to parse base64 string.");
        }
        return code;
    }

    public static bytesToBase64(bytes: number[] | Uint8Array) {
        const base64abc = [
            "A", "B", "C", "D", "E", "F", "G", "H", "I", "J", "K", "L", "M",
            "N", "O", "P", "Q", "R", "S", "T", "U", "V", "W", "X", "Y", "Z",
            "a", "b", "c", "d", "e", "f", "g", "h", "i", "j", "k", "l", "m",
            "n", "o", "p", "q", "r", "s", "t", "u", "v", "w", "x", "y", "z",
            "0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "+", "/"
        ];

        let result = '', i, l = bytes.length;
        for (i = 2; i < l; i += 3) {
            result += base64abc[bytes[i - 2] >> 2];
            result += base64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)];
            result += base64abc[((bytes[i - 1] & 0x0F) << 2) | (bytes[i] >> 6)];
            result += base64abc[bytes[i] & 0x3F];
        }
        if (i === l + 1) { // 1 octet yet to write
            result += base64abc[bytes[i - 2] >> 2];
            result += base64abc[(bytes[i - 2] & 0x03) << 4];
            result += "==";
        }
        if (i === l) { // 2 octets yet to write
            result += base64abc[bytes[i - 2] >> 2];
            result += base64abc[((bytes[i - 2] & 0x03) << 4) | (bytes[i - 1] >> 4)];
            result += base64abc[(bytes[i - 1] & 0x0F) << 2];
            result += "=";
        }
        return result;
    }

    public static base64ToBytes(str: string) {
        if (str.length % 4 !== 0) {
            throw new Error("Unable to parse base64 string.");
        }
        const index = str.indexOf("=");
        if (index !== -1 && index < str.length - 2) {
            throw new Error("Unable to parse base64 string.");
        }
        let missingOctets = str.endsWith("==") ? 2 : str.endsWith("=") ? 1 : 0,
            n = str.length,
            result = new Uint8Array(3 * (n / 4)),
            buffer: number;
        for (let i = 0, j = 0; i < n; i += 4, j += 3) {
            buffer =
                Util.getBase64Code(str.charCodeAt(i)) << 18 |
                Util.getBase64Code(str.charCodeAt(i + 1)) << 12 |
                Util.getBase64Code(str.charCodeAt(i + 2)) << 6 |
                Util.getBase64Code(str.charCodeAt(i + 3));
            result[j] = buffer >> 16;
            result[j + 1] = (buffer >> 8) & 0xFF;
            result[j + 2] = buffer & 0xFF;
        }
        return result.subarray(0, result.length - missingOctets);
    }

    public static async generateSymmetricKey(): Promise<CryptoKey> {
        return await crypto.subtle.generateKey(
            {
                name: "AES-GCM",
                length: 256,
            },
            true,
            ["encrypt", "decrypt"]
        );
    }

    public static async encryptWithSymmetricKey(key: CryptoKey, data: string) {
        const iv = crypto.getRandomValues(new Uint8Array(12));
        const dataBuffer = new TextEncoder().encode(data);

        const encryptedData = await crypto.subtle.encrypt(
            {
                name: "AES-GCM",
                iv: iv,
            },
            key,
            dataBuffer
        );

        // Extract the authentication tag (last 16 bytes of the encrypted data)
        const tag = encryptedData.slice(-16);

        return {
            iv: iv,
            encryptedData: encryptedData.slice(0, -16), // Remove the authentication tag from the encrypted data
            tag: tag // Return the authentication tag
        };
    }

    /*******************************************/
    /***************** Dialogs *****************/
    /*******************************************/

    /**
    * Hides a dialog box by fading it out gradually.
    * @param {string} dialogID - The ID of the dialog element to hide.
    * @param {boolean} [destroy] - Whether to remove the dialog element from the DOM after hiding it.
    * @returns {void}
    */
    public static DialogFadeOut(dialogID: string, destroy?: boolean): void {
        const dialog = document.getElementById(dialogID);
        if (!dialog) return;

        dialog.classList.remove("opacity1");
        dialog.classList.add("fadeout");

        setTimeout(
            () => {
                const dialogInner = document.getElementById(dialogID) as HTMLElement;
                if (dialogInner) {
                    //@ts-ignore
                    dialogInner.close();
                    dialogInner.classList.remove("fadeout");

                    const focusId = dialogInner.getAttribute("data-focus-element-id");
                    if (focusId) {
                        const focusElement = document.getElementById(focusId) as HTMLInputElement;
                        if (focusElement) {
                            focusElement.focus();
                            try { focusElement.select(); } catch { }
                        }
                    }
                    if (destroy)
                        dialogInner.remove();
                }

                if (this.GetOpenedDialogCount() === 0)
                    this.DialogClosed();
            },
            500);
    }

    private static GetOpenedDialogCount(): number {
        let count = 0;
        const dialogs = Array.from(document.getElementsByTagName('dialog'));
        for (const dialog of dialogs) {
            if (dialog.open)
                count++;
        }
        return count;
    }

    /**
    * Handles the logic when a dialog is closed.
    * @returns {void}
    */
    public static DialogClosed(): void {
        if (this.GetOpenedDialogCount() !== 0)
            return;

        document.body.style.overflow = "auto";
        document.getElementsByTagName("html")[0].style.paddingRight = "0";
        document.getElementsByTagName("header")[0].style.paddingRight = "0";
        const dCats = document.getElementById("div_categories");
        if (dCats)
            dCats.style.paddingRight = "0";

        const divlang = document.getElementById("div_lang");
        if (divlang)
            divlang.style.paddingRight = "0";
    }

    /**
    * Logs an error to the server along with its location and message.
    * @param {string} location - The location where the error occurred.
    * @param {Error} error - The error object to log.
    * @returns {void}
    */
    public static LogError(location: string, error: Error, localAolo: IaOLO): void {
        this.LogErrors(`error at >> ${location} >> ${error.message}`, error, localAolo);
    }

    /**
    * Logs an error message and stack trace to the server.
    * @param {string} msg - The error message to log.
    * @param {Error} error - The error object containing the stack trace.
    * @returns {boolean} - Returns false.
    */
    public static LogErrors(msg: string, error: Error, localAolo: IaOLO): boolean {
        return this.LogErrorString(msg, error.stack ? error.stack : '', localAolo);
    }

    /**
    * Logs an error to the server along with its message and stack trace.
    * @param {string} msg - The error message to log.
    * @param {string} stack - The stack trace of the error to log.
    * @returns {boolean} Returns false to indicate that the error was logged successfully.
    */
    private static LogErrorString(msg: string, stack: string, localAolo: IaOLO): boolean {
        try {
            if (msg.indexOf("home/JSError") > -1)
                return false;

            if (msg.indexOf("home/GetDateTime") > -1)
                return false;

            if (stack.indexOf("gtm") > -1)
                return false;

            if (!localAolo.Temp)
                return false;

            if (!localAolo.Temp.LastLoggedErrorTime) {
                localAolo.Temp.LastLoggedErrorTime = 0;
                localAolo.Temp.LastLoggedErrorMsg = "";
            }

            const errorTime = new Date();
            const errorMilliSec = errorTime.getTime();

            if (localAolo.Temp.LastLoggedErrorTime + 5000 < errorMilliSec
                || localAolo.Temp.LastLoggedErrorMsg !== msg) {

                const err = {
                    sky: "", //localAolo.storeInfo.StoreKey,
                    sdt: "", //Util.formatDateTimeSeconds(this.NowStore(localAolo.Temp.TimeOffset)),
                    loc: "Online order",
                    msg: msg,
                    dtl: "",
                    stk: stack,
                    brws: this.GetBrowserName(),
                    oguid: "" //(localAolo.Order?.GUID) ? localAolo.Order.GUID : "",
                };

                //localAolo.Modules.OnlineOrderingService.postJSError(JSON.stringify(err), localAolo);
            }
            localAolo.Temp.LastLoggedErrorTime = errorMilliSec;
            localAolo.Temp.LastLoggedErrorMsg = msg;
        } catch (ex: unknown) {
            const ex1 = ex;
        }
        return false;
    }

    /**
    * Checks if a value is a valid password.
    * @param {string} value - The value to check.
    * @returns {boolean} - True if the value is a valid password, false otherwise.
    */
    public static CheckIsPassword(value: string): boolean {
        const filter = /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[*+@#$%&!])[0-9a-zA-Z*+@#$%&!]{8,20}$/;
        return String(value).search(filter) != -1;
    }

    /**
    * Checks if the provided value is a valid email address.
    * @param {string} value - The email address to validate.
    * @returns {boolean} - Whether the provided value is a valid email address.
    */
    // public static IsValidEmail(value: string): boolean {
    //     const filter = /^\s*[\w\-\+_]+(\.[\w\-\+_]+)*\@[\w\-\+_]+\.[\w\-\+_]+(\.[\w\-\+_]+)*\s*$/;
    //     return String(value).search(filter) !== -1;
    // }

    /**
    * Fixes the case of the provided string by capitalizing the first letter of each word and lowercasing the rest.
    * @param {string} value - The string to fix the case of.
    * @returns {string} - The fixed string.
    */
    public static FixStringCase(value: string): string {
        const words = value.split(" ");
        let result = "";
        words.forEach(word => {
            if (word) {
                const firstChar = word.charAt(0).toUpperCase();
                const restOfWord = word.substring(1).toLowerCase();
                result += firstChar + restOfWord + ' ';
            }
        });
        return result.trim();
    }

    /**
    * Checks if the provided value is a number.
    * @param {(string|number)} value - The value to check.
    * @returns {boolean} - Whether the provided value is a number.
    */
    private static CheckIsNumber(value: string | number): boolean {
        if (typeof (value) == 'number') {
            value = value.toString();
        }
        if (typeof (value) == 'string') {
            if (this.CheckMinLength(value, 1)) {
                return !isNaN(Number(value));
            } else {
                return false;
            }
        }
        return false;
    }

    /**
    * Checks if the provided value is at least the provided minimum length.
    * @param {*} value - The value to check.
    * @param {number} minLength - The minimum length that the value must be.
    * @returns {boolean} - Whether the provided value is at least the provided minimum length.
    */
    private static CheckMinLength(value: any, minLength: number): boolean {
        if (value != undefined)
            return (value.length >= minLength);
        else
            return false;
    }

    /**
    * Calculates the distance between two sets of latitude and longitude coordinates
    * using the Haversine Distance formula and returns the result in the specified unit.
    * @param {number} lat1 - The latitude of the first point in degrees.
    * @param {number} lon1 - The longitude of the first point in degrees.
    * @param {number} lat2 - The latitude of the second point in degrees.
    * @param {number} lon2 - The longitude of the second point in degrees.
    * @param {string} unit - The unit in which to return the distance, one of "M" for miles, 
    *                        "K" for kilometers, "N" for nautical miles, or "F" for feet.
    * @returns {number} - The calculated distance between the two points in the specified unit.
    */
    public static GetDistance(lat1: number, lon1: number, lat2: number, lon2: number, unit: string): number {
        //Haversine Distance calculation
        if (lat1 === lat2 && lon1 === lon2) {
            return 0;
        }
        else {
            const radlat1 = Math.PI * lat1 / 180;
            const radlat2 = Math.PI * lat2 / 180;
            const theta = lon1 - lon2;
            const radtheta = Math.PI * theta / 180;
            let dist = Math.sin(radlat1) * Math.sin(radlat2) + Math.cos(radlat1) * Math.cos(radlat2) * Math.cos(radtheta);
            if (dist > 1) {
                dist = 1;
            }
            dist = Math.acos(dist);
            dist = dist * 180 / Math.PI;
            dist = dist * 60 * 1.1515;                      // Miles
            if (unit === "K") { dist = dist * 1.609344; }   // Kilometers
            if (unit === "N") { dist = dist * 0.8684; }     // Nautical miles
            if (unit === "F") { dist = dist * 5280; }       // Feet
            return this.Float2(dist);
        }
    }

    /**
     * Determines the appropriate unit label for the distance based on measurement system.
     * @param {number} distance - The distance value.
     * @param {boolean} isMetric - Whether the metric system is used.
     * @returns {string} The unit label ("mile", "miles", "kilometer", "kilometers").
     */
    public static readonly GetDistanceUnits = (distance: number, isMetric: boolean): string => {
        if (isMetric)
            return distance === 1 ? "kilometer" : "kilometers";

        return distance === 1 ? "mile" : "miles";
    };

    /**
    * Converts miles to kilometers
    *
    * @param {Number} miles - number of miles to convert to kilometers.
    * @returns {Number} - The number of kilometers.
    */
    public static ConvertMilesToKilometers(miles: number): number {
        const conversionFactor = 1.60934;
        return (miles * conversionFactor);
    }

    public static IsHoliday(iDate: Date, storeBusinessDate: string, timeOffset: number): number {
        const hDate = new Date(iDate.getFullYear(), iDate.getMonth(), iDate.getDate());
        let holidayID = 0;
        const iYear = hDate.getFullYear();
        const newYearDay = new Date(iYear, 0, 1);
        const valentinesDay = new Date(iYear, 1, 14);
        const easterDate = this.GetEasterDate(iYear);
        const independenceDay = new Date(iYear, 6, 4);
        let memorialDay = new Date(iYear, 4, 31);
        const christmasEve = new Date(iYear, 11, 24);
        const christmas = new Date(iYear, 11, 25);
        const newYearEve = new Date(iYear, 11, 31);
        const superBowl = this.GetSuperBowlDate(iYear, storeBusinessDate, timeOffset);
        let dayOfWeek = memorialDay.getDay();
        while (dayOfWeek !== 1) {
            memorialDay = this.DateAdd(memorialDay, -1, "d");
            dayOfWeek = memorialDay.getDay();
        }
        let laborDay = new Date(iYear, 8, 1);
        dayOfWeek = laborDay.getDay();
        while (dayOfWeek !== 1) {
            laborDay = this.DateAdd(laborDay, 1, "d");
            dayOfWeek = laborDay.getDay();
        }
        const halloween = new Date(iYear, 9, 31);
        let thanksgivingDay = new Date(iYear, 10, 1);
        let mondayCount = 0;
        for (let i = 1; i < 31; i++) {
            thanksgivingDay = new Date(iYear, 10, i);
            dayOfWeek = thanksgivingDay.getDay();
            if (dayOfWeek === 4) {
                if (mondayCount === 3) {
                    break;
                }
                else {
                    mondayCount += 1;
                }
            }
        }
        if (newYearDay.getTime() == hDate.getTime()) {
            holidayID = 100;// New Year Day
        } else if (valentinesDay.getTime() == hDate.getTime()) {
            holidayID = 150;// valentinesDay
        } else if (easterDate.getTime() == hDate.getTime()) {
            holidayID = 200;// Easter
        } else if (memorialDay.getTime() == hDate.getTime()) {
            holidayID = 300;// Memorial Day
        } else if (independenceDay.getTime() == hDate.getTime()) {
            holidayID = 400;// independence Day
        } else if (laborDay.getTime() == hDate.getTime()) {
            holidayID = 500;
        } else if (halloween.getTime() == hDate.getTime()) {
            holidayID = 550;
        } else if (thanksgivingDay.getTime() == hDate.getTime()) {
            holidayID = 600;
        } else if (christmasEve.getTime() == hDate.getTime()) {
            holidayID = 700;
        } else if (christmas.getTime() == hDate.getTime()) {
            holidayID = 800;
        } else if (newYearEve.getTime() == hDate.getTime()) {
            holidayID = 900;
        } else if (superBowl && superBowl.getTime() == hDate.getTime()) {
            holidayID = 950;
        }
        return holidayID;
    }

    private static GetEasterDate(year: number): Date {
        let day: number;
        let month: number;
        const goldenNumber = year % 19;
        const century = Math.floor(year / 100);
        const paschalFullMoon = (century - Math.floor(century / 4) - Math.floor((8 * century + 13) / 25) + 19 * goldenNumber + 15) % 30;
        const adjustment = paschalFullMoon - Math.floor(paschalFullMoon / 28) * (1 - Math.floor(paschalFullMoon / 28) * Math.floor(29 / (paschalFullMoon + 1)) * Math.floor((21 - goldenNumber) / 11));
        day = adjustment - ((year + Math.floor(year / 4) + adjustment + 2 - century + Math.floor(century / 4)) % 7) + 28;

        // Step 6: The month is fixed to March (3), but we need to adjust if the day is greater than 31 (April)
        month = 3; // March
        if (day > 31) {
            month++; // Switch to April
            day -= 31; // Adjust the day
        }
        return new Date(year, month - 1, Math.floor(day));
    }

    private static GetSuperBowlDate(year: number, storeBusinessDate: string, timeOffset: number): Date {
        if (storeBusinessDate)
            return this.StoreDate(storeBusinessDate, timeOffset);

        switch (year) {
            case 2018:
                return new Date(year, 1, 4);
            case 2019:
                return new Date(year, 1, 3);
            case 2020:
                return new Date(year, 1, 2);
            case 2021:
                return new Date(year, 1, 7);
            case 2022:
                return new Date(year, 1, 13);
            case 2023:
                return new Date(year, 1, 12);
            case 2024:
                return new Date(year, 1, 11);
        }
        return new Date();
    }

    private static StoreDate(dateString: string, timeOffset: number): Date {
        let iDate: string[] | null = null;
        let iTime: string[] | null = null;
        if (!dateString) {
            return this.NowStore(timeOffset);
        }
        if (dateString.indexOf("T") > -1) {
            const spDateTime = dateString.split("T");
            iDate = spDateTime[0].split("-");
            iTime = spDateTime[1].split(":");
        } else if (dateString.indexOf(" ") > -1) {
            const spDateTime = dateString.split(" ");
            if (spDateTime[0].indexOf("/") > -1) {
                iDate = spDateTime[0].split("/");
                const yearVal = iDate[2];
                iDate[2] = iDate[1];
                iDate[1] = iDate[0];
                iDate[0] = yearVal;
            } else {
                iDate = spDateTime[0].split("-");
            }
            iTime = spDateTime[1].split(":");
            if (spDateTime.length > 2) {
                const ampm = spDateTime[2];
                const hr = parseInt(iTime[0]);
                if (ampm.toLowerCase() == 'pm' && hr >= 1 && hr < 12) {
                    iTime[0] = (hr + 12).toString();
                } else if (ampm.toLowerCase() == 'am' && hr == 12) {
                    iTime[0] = '0';
                }
            }
        } else if (dateString.indexOf("-") > -1) {
            iDate = dateString.split("-");
        } else if (dateString.indexOf(" ") > -1 || dateString.indexOf(":") > -1) {
            iTime = dateString.split(":");
        }
        let iYear = 1970;
        let iMonth = 1;
        let iDay = 1;
        let iHour = 0;
        let iMinute = 0;
        let iSecond = 0;
        if (iDate) {
            iYear = parseInt(iDate[0]);
            iMonth = parseInt(iDate[1]) - 1;
            iDay = parseInt(iDate[2]);
        }
        if (iTime) {
            iHour = parseInt(iTime[0]);
            iMinute = parseInt(iTime[1]);
            iSecond = parseInt(iTime[2]);
        }
        let nowDateTime = new Date();
        if (!iDate && !iTime && dateString != "") {
            try {
                nowDateTime = new Date(dateString);
            } catch (ex) { }
        } else {
            nowDateTime = new Date(iYear, iMonth, iDay, iHour, iMinute, iSecond, 0);
        }
        return nowDateTime;
    }

    /**
    * Rounds a floating point number to the specified number of decimal places and returns the result.
    * @param {string|number} num - The number to round.
    * @param {number} decimalPlaces - The number of decimal places to round to.
    * @returns {number} - The resulting rounded number.
    */
    public static FloatX(num: string | number, decimalPlaces: number): number {
        const number = this.FloatRemove_e(Number(num));
        return +(Math.round(Number(number + "e+" + decimalPlaces)) + "e-" + decimalPlaces);
    }

    /**
    * Removes the "e" notation from a floating point number and returns the result as a float.
    * @param {number} value - The value to remove "e" notation from.
    * @returns {number} - The resulting float value.
    */
    private static FloatRemove_e(value: number): number {
        let stringValue = parseFloat(value.toFixed(13)) + " ";
        if (stringValue.indexOf("e") === -1) {
            return parseFloat(stringValue);
        }
        for (let i = 13; i > 0; i--) {
            stringValue = parseFloat(value.toFixed(i)) + " ";
            if (stringValue.indexOf("e") === -1) {
                break;
            }
        }
        return parseFloat(stringValue);
    }

    /**
    * Converts a value to a floating point number.
    * @param {string|number} value - The value to convert to a floating point number.
    * @returns {number} - The resulting floating point number, or 0 if the value cannot be converted.
    */
    public static FloatNum(value: string | number): number {
        let num = 0;
        try {
            switch (typeof value) {
                case "string":
                    if (this.CheckIsNumber(value)) {
                        num = parseFloat(value);
                    }
                    break;
                case "number":
                    num = value;
                    break;
            }
        }
        catch (ex) {
            num = 0;
        }
        return num;
    }

    /**
    * Rounds a floating point number to 2 decimal places and returns the result.
    * @param {string|number} value - The number to round.
    * @returns {number} - The resulting rounded number.
    */
    public static Float2(value: string | number): number {
        let returnValue = 0.0;
        const num = Util.FloatNum(value);
        returnValue = Util.FloatX(num, 2);
        return returnValue;
    }

    /**
    * Rounds a floating point number to 5 decimal places and returns the result.
    * @param {string|number} value - The number to round.
    * @returns {number} - The resulting rounded number.
    */
    public static Float5(value: string | number): number {
        let returnValue = 0.0;
        const num = Util.FloatNum(value);
        returnValue = Util.FloatX(num, 5);
        return returnValue;
    }

    public static Float12(value: string | number): number {
        let returnValue = 0.0;
        const num = Util.FloatNum(value);
        returnValue = Util.FloatX(num, 12);
        return returnValue;
    }

    private static Repeat(text: string, length: number): string {
        length = length || 1;
        return Array(length + 1).join(text);
    }

    public static Right(text: string, maxLength: number, fillWith: string): string {
        if (!fillWith) fillWith = " ";
        const txt = this.Repeat(fillWith, maxLength) + text;
        return txt.substring(txt.length - maxLength);
    }

    public static RightText(text: string, length: number): string {
        const tLen = text.length;
        if (tLen < length) {
            return text;
        } else {
            return text.substring(tLen - length);
        }
    }

    /**
    * Returns the current date and time, accounting for a specified time offset.
    * @returns {Date} The current date and time, accounting for a specified time offset.
    */
    public static NowStore(timeOffset: number): Date {
        if (localStorage.tempNow)
            return new Date(localStorage.tempNow); // '9/14/2024 02:55:00 GMT-0700 (Pacific Daylight Time)'); // iDate
        const date = new Date();
        const date_ms = date.getTime();
        const nowDateTime = new Date(date_ms + timeOffset);
        return nowDateTime;
    }

    /**
     * Computes the business date for a given date/time, adjusted for the store's 
     * time offset and business day start minute (BSM).
     *
     * The returned date is normalized to midnight, offset backward by the 
     * business day start minute, and then shifted forward to the exact start 
     * of the business day.
     *
     * @param {Date} dateTime - The reference date/time to compute from.
     * @param {number} timeOffset - The time offset (in milliseconds) applied to adjust store time.
     * @param {number} businessDayStartMinute - The number of minutes after midnight when the business day starts.
     * @returns {Date} A Date object representing the computed business date at the business start minute.
     */
    public static GetBUSDate(dateTime: Date, timeOffset: number, businessDayStartMinute: number): Date {
        let bDateTime = Util.NowStore(timeOffset);

        if (dateTime instanceof Date)
            bDateTime = dateTime;

        bDateTime = Util.DateAdd(bDateTime, -businessDayStartMinute, "m");
        return Util.DateAdd(new Date(bDateTime.setHours(0, 0, 0, 0)), businessDayStartMinute, 'm');
    }

    /**
    * Returns the name of the browser being used.
    * @returns {string} The name of the browser being used.
    */
    private static GetBrowserName(): string {
        const userAgent = navigator.userAgent;

        if (userAgent.indexOf("Firefox") > -1) {
            return "Firefox";
        } else if (userAgent.indexOf("Chrome") > -1) {
            return "Chrome";
        } else if (userAgent.indexOf("Safari") > -1) {
            return "Safari";
        } else if (userAgent.indexOf("MSIE") > -1 || userAgent.indexOf("Trident") > -1) {
            return "Internet Explorer";
        } else if (userAgent.indexOf("Edge") > -1) {
            return "Edge";
        } else if (userAgent.indexOf("Opera") > -1) {
            return "Opera";
        } else {
            return "Unknown";
        }
    }

    public static isIncompatibleBrowser(): boolean {
        const userAgent = window.navigator.userAgent;
        const isIE = userAgent.indexOf("MSIE ") > -1 || userAgent.indexOf("Trident/") > -1;
        const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);

        if (isIE) return true;

        if (isSafari) {
            const safariVersion = userAgent.match(/Version\/(\d+\.\d+)/);
            if (safariVersion && safariVersion.length > 1) {
                const version = parseFloat(safariVersion[1]);
                return version < 14;
            }
        }

        return false;
    }

    /**
    * Hides the element with the given ID by adding the "hidden" class to its class list.
    * @param {string} elementId - The ID of the element to hide.
    * @returns {void}
    */
    public static hideElement(element: string | HTMLElement): void {
        const elementToModify = (typeof element === "string" ? document.getElementById(element) : element) as HTMLElement;
        if (elementToModify) {
            elementToModify.classList.add("hidden");
            elementToModify.setAttribute("aria-hidden", "true");
        }
    }

    /**
    * Shows the element with the given ID by removing the "hidden" class from its class list.
    * @param {string} elementId - The ID of the element to show.
    * @returns {void}
    */
    public static showElement(element: string | HTMLElement): void {
        const elementToModify = (typeof element === "string" ? document.getElementById(element) : element) as HTMLElement;
        if (elementToModify) {
            elementToModify.classList.remove("hidden");
            elementToModify.removeAttribute("aria-hidden");
        }
    }

    /**
    * Visibility off the element with the given ID by adding the "none-visibility" class to its class list.
    * @param {string} elementId - The ID of the element to hide.
    * @returns {void}
    */
    public static visibilityOffElement(element: string | HTMLElement): void {
        const elementToModify = (typeof element === "string" ? document.getElementById(element) : element) as HTMLElement;
        if (elementToModify)
            elementToModify.classList.add("none-visibility");
    }

    /**
    * Shows the element with the given ID by removing the "none-visibility" class from its class list.
    * @param {string} elementId - The ID of the element to show.
    * @returns {void}
    */
    public static visibilityOnElement(element: string | HTMLElement): void {
        const elementToModify = (typeof element === "string" ? document.getElementById(element) : element) as HTMLInputElement;
        if (elementToModify)
            elementToModify.classList.remove("none-visibility");
    }

    /**
    * Gets the value of the element with the given ID.
    * @param {string} elementId - The ID of the element to get the value of.
    * @returns {string} The value of the element.
    */
    public static getElementValue(element: string | HTMLElement): string {
        const elementToModify = (typeof element === "string" ? document.getElementById(element) : element) as HTMLInputElement;
        if (elementToModify)
            return elementToModify.value;
        return "";
    }

    public static getElementChecked(elementId: string): boolean {
        const element = document.getElementById(elementId) as HTMLInputElement;
        if (element)
            return element.checked;
        return false;
    }

    /**
    * Sets the value of the given element using the specified method and value.
    * @param {string} method - The method to use to set the value of the element. Can be one of "VALUE", "CHECKED", "DISABLED", "INNERHTML", "ONCLICK", "ONCHANGE", or "ONINPUT". 
    *                          If the method is not recognized, it will be treated as an attribute name and set using the specified value.
    * @param {string | HTMLElement} element - The ID of the element to set the value of or a reference to the element itself.
    * @param {*} value - The value to set the element to.
    * @returns {void}
    */
    public static setElement(method: string, element: string | HTMLElement, value: any, includeMouseEvent: boolean = false): void {
        const elementToModify = (typeof element === "string" ? document.getElementById(element) : element) as HTMLInputElement;
        if (elementToModify) {
            switch (method.toUpperCase()) {
                case "VALUE":
                    elementToModify.value = value.toString();
                    break;
                case "CHECKED":
                    elementToModify.checked = value;
                    break;
                case "DISABLED":
                    elementToModify.disabled = value;
                    break;
                case "INNERHTML":
                    elementToModify.innerHTML = value;
                    break;
                case "INNERTEXT":
                    elementToModify.innerText = value;
                    break;
                case "ONCLICK":
                    if (value == null)
                        elementToModify.onclick = null;
                    else {
                        elementToModify.onclick = (e: MouseEvent) => {
                            if (typeof value === "function") {
                                if (includeMouseEvent)
                                    value(e);
                                else
                                    value();
                            } else if (Array.isArray(value)) {
                                value[0](...value.slice(1));
                            }
                        };
                    }
                    break;
                case "ONCHANGE":
                    if (value == null)
                        elementToModify.onchange = null;
                    else
                        elementToModify.onchange = function () {
                            if (typeof value === "function") {
                                value();
                            } else if (Array.isArray(value)) {
                                value[0](...value.slice(1));
                            }
                        };
                    break;
                case "ONINPUT":
                    if (value == null)
                        elementToModify.oninput = null;
                    else
                        elementToModify.oninput = function () {
                            if (typeof value === "function") {
                                value();
                            } else if (Array.isArray(value)) {
                                value[0](...value.slice(1));
                            }
                        };
                    break;

                case "ONBLUR":
                    if (value == null)
                        elementToModify.onblur = null;
                    else
                        elementToModify.onblur = function () {
                            if (typeof value === "function") {
                                value();
                            } else if (Array.isArray(value)) {
                                value[0](...value.slice(1));
                            }
                        };
                    break;

                case "ONFOCUS":
                    if (value == null)
                        elementToModify.onfocus = null;
                    else
                        elementToModify.onfocus = function () {
                            if (typeof value === "function") {
                                value();
                            } else if (Array.isArray(value)) {
                                value[0](...value.slice(1));
                            }
                        };
                    break;
                default:
                    elementToModify.setAttribute(method, value);
                    break;
            }
        }
    }

    /**
    * Sets or removes a CSS class from an element based on the method parameter.
    * @param {string} method - The method to be performed. Can be either "ADD" or "REMOVE".
    * @param {string} elementId - The ID of the element to be targeted.
    * @param {string} value - The CSS class to be added or removed.
    * @returns {void}
    */
    public static setElementClass(method: string, elementId: string, value: string): void {
        const element = document.getElementById(elementId) as HTMLInputElement;
        if (element) {
            switch (method.toUpperCase()) {
                case "ADD":
                    element.classList.add(value);
                    break;
                case "REMOVE":
                    element.classList.remove(value);
                    break;
            }
        }
    }

    /**
    * Removes all non-numeric characters from a string.
    * @param {string} value - The string to be cleaned.
    * @returns {string} - The cleaned string.
    */
    public static cleanNonDigits(value: string): string {
        return value.replace(/\D/g, "");
    }

    /**
    * Formats a phone number into a standardized format.
    * @param {string} phoneNumber - The phone number to be formatted.
    * @returns {string} - The formatted phone number.
    */
    public static formatPhoneNumber(phoneNumber: string, format: string): string {
        // Remove all non-digit characters from the phone number
        const cleaned = phoneNumber.replace(/\D/g, '');

        // If there are no digits in the cleaned phone number, return an empty string
        if (cleaned.length === 0) {
            return '';
        }

        let formattedNumber = '';
        let index = 0;

        // Iterate over the format string
        for (let i = 0; i < format.length; i++) {
            // If the current character is 'X', replace it with the corresponding digit from the phone number
            if (format[i] === 'X') {
                if (index < cleaned.length) {
                    formattedNumber += cleaned[index];
                    index++;
                } else {
                    // If there are no more digits in the phone number, break out of the loop
                    break;
                }
            } else {
                // If the current character is not 'X', simply append it to the formatted number
                formattedNumber += format[i];
            }
        }

        return formattedNumber;
    }

    /**
    * Formats a phone number as the user types.
    * @param {string} id - The ID of the input element.
    * @param {string} key - The key that was pressed.
    * @returns {void}
    */
    public static formatPhoneNumberAuto(id: string, isoCode: string | null): void {
        const textbox = document.getElementById(id) as HTMLInputElement;
        if (!textbox)
            return;

        let input = textbox.value;

        // Strip all characters from the input except digits
        input = input.replace(/\D/g, '');

        const format = Util.getPhoneFormat(input, isoCode);
        textbox.value = Util.formatPhoneNumber(input, format);
    }

    public static GetBrowserLanguage(): string {
        return navigator.language;
    }

    public static formatPhoneNumberByCountryId(phoneNumber: string, countryId: number, countries: IDataCountry[]): string {
        const isoCode = Util.getCountryIsoCode(countryId, countries);
        const format = Util.getPhoneFormat(phoneNumber, isoCode);
        const formattedPhone = Util.formatPhoneNumber(phoneNumber, format);
        return formattedPhone;
    }

    public static getCountryIsoCode(countryId: number, countries: IDataCountry[]): string {
        const country = countries.find(x => x.CountryID === countryId);
        if (!country)
            return "US";

        return country.IsoCode;
    }

    /** *
     * Cleans a given phone number to just digits.
     * 
     * @param { string | null | undefined } isoCode The iso code used to determine the phone format.
     * @returns { string } The phone format corresponding the the isoCode.
     */
    public static getPhoneFormat(phoneNumber: string, isoCode: string | null | undefined): string {
        switch (isoCode) {
            case "US":
                return "(XXX) XXX-XXXX";
            case "MX":
                if (phoneNumber.startsWith("55") ||
                    phoneNumber.startsWith("56") ||
                    phoneNumber.startsWith("81") ||
                    phoneNumber.startsWith("33"))
                    return "XX XXXX XXXX";
                else
                    return "XXX XXX XX XX";
            default:
                return "(XXX) XXX-XXXX";
        }
    }

    public static populateCountryCodeSelectList(selectElementId: string, phoneElementId: string, countries: IDataCountry[], selectedValue?: number, applyFormat: boolean = true): void {
        const select = document.getElementById(selectElementId) as HTMLSelectElement;
        if (!select)
            return;

        select.innerHTML = "";
        for (const country of countries) {
            const option = document.createElement("option") as HTMLOptionElement;
            option.value = country.CountryID.toString();
            option.textContent = `${country.IsoCode} (${country.CountryCode})`;
            option.dataset.isoCode = country.IsoCode;
            option.dataset.name = `${country.Name} (${country.CountryCode})`; // Set selected display value
            option.dataset.abr = `${country.IsoCode} (${country.CountryCode})`; // Set selected display value
            select.appendChild(option); // Append the option to the select element
        }

        if (selectedValue) {
            setTimeout(() => {
                Util.setElement("value", select, selectedValue.toString());
            }, 100);

            if (applyFormat)
                Util.populateCountryCodeSelectList_PhoneFormat(phoneElementId, countries, selectedValue);
        }

        select.onfocus = () => {
            const options = Array.from(select.options);
            for (const option of options) {
                option.textContent = option.dataset.name || option.text;
            }
        };

        select.onblur = () => {
            const options = Array.from(select.options);
            for (const option of options) {
                option.textContent = option.dataset.abr || option.text;
            }
        };

        select.onchange = async () => {
            const options = Array.from(select.options);
            for (const option of options) {
                option.textContent = option.dataset.abr || option.text;
            }

            if (applyFormat)
                Util.populateCountryCodeSelectList_PhoneFormat(phoneElementId, countries, Number(select.value));

            const phoneInput = document.getElementById(phoneElementId) as HTMLSelectElement;
            if (phoneInput)
                Util.setFocus(phoneInput);
        }
    }

    public static setFocus(element: string | HTMLElement): void {
        const elementToModify = (typeof element === "string" ? document.getElementById(element) : element) as HTMLInputElement;
        if (elementToModify)
            elementToModify.focus();
    }

    public static addKeyboardActivation(element: HTMLElement | null, handler?: (event: Event) => void): void {
        if (!element) return;
        element.addEventListener("keydown", (event: KeyboardEvent) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                handler ? handler(event) : element.click();
            }
        });
    }

    public static populateCountryCodeSelectList_PhoneFormat(phoneElementId: string, countries: IDataCountry[], countryCodeId: number): void {
        const phoneInput = document.getElementById(phoneElementId) as HTMLSelectElement;
        if (!phoneInput)
            return;

        const phoneCountry = countries.find(x => x.CountryID === countryCodeId) || null;
        const isoCode = phoneCountry?.IsoCode || null;
        const newPhoneFormat = Util.getPhoneFormat(Util.getElementValue(phoneElementId).replace(/\D/g, ''), isoCode);

        phoneInput.value = Util.formatPhoneNumber(phoneInput.value, newPhoneFormat);
        phoneInput.setAttribute('placeholder', Util.formatPhoneNumber("5555555555", newPhoneFormat));

        phoneInput.onkeyup = (event) => {
            const key = event.key;
            if (key !== "Backspace" && key !== "Delete")
                Util.formatPhoneNumberAuto(phoneInput.id, isoCode);
        }
    }

    /**
    * Creates an HTMLElement from an HTML string.
    * 
    * @param {string} htmlContent - The HTML string to be transformed into an HTMLElement.
    * @returns {HTMLElement} The first child of the 'div' containing the supplied HTML string.
    */
    public static createHtmlElementFromTemplate(htmlContent: string): HTMLElement {
        const template = document.createElement("div") as HTMLElement;
        template.innerHTML = htmlContent;
        if (template.firstChild)
            return template.firstElementChild as HTMLElement;
        return document.createElement("div");
    }

    /**
    * Converts an array of IName or IDescription objects to a URI encoded JSON string.
    *
    * @param {IName[] | IDescription[] | INewName[]} items - The array of items to be encoded.
    * @returns {string} The URI encoded JSON string representation of the supplied array.
    */
    public static toLtagj(items: IName[] | IDescription[] | INewName[]): string {
        const string = JSON.stringify(items);
        return encodeURIComponent(string);
    }

    /**
    * Checks if the current environment is a WebView.
     *
    * @returns {boolean} true if the environment is likely a WebView, false otherwise.
     */
    public static isAppView(): boolean {
        return localStorage.getItem("appView") === "true";
    }

    /**
     * Looks up a query string parameter by name, case-insensitively on the parameter name.
     * URLSearchParams.get() is case-sensitive on the key, unlike ASP.NET Core's query string
     * model binding -- a request built with a different key casing (e.g. ?mobile=true instead
     * of ?Mobile=true) silently misses a plain `.get("Mobile")` call.
     *
     * @param {string} search - A query string, e.g. window.location.search.
     * @param {string} name - The parameter name to look up.
     * @returns {string | null} The first matching value, or null if not present.
     */
    public static getQueryParamCaseInsensitive(search: string, name: string): string | null {
        const lowerName = name.toLowerCase();
        for (const [key, value] of new URLSearchParams(search).entries()) {
            if (key.toLowerCase() === lowerName)
                return value;
        }
        return null;
    }

    /**
     * Determines whether the current platform is iOS.
     * 
     * @param {AppPlatform?} platform - The platform identifier (from config or context).
     * @returns {boolean} `true` if the platform is iOS or user agent matches iOS WebKit without Safari.
     */
    public static isiOS(platform?: AppPlatform): boolean {
        if (platform === AppPlatform.IOS)
            return true;

        // Check for iOS WebView (e.g., in-app browser on iPhone/iPad)
        return /(iPhone|iPod|iPad).*AppleWebKit(?!.*Safari)/i.test(navigator.userAgent);
    }

    /**
     * Detects if the current browser is Safari running on an iOS device.
     *
     * @returns {boolean} True if the current environment is iOS Safari, otherwise false.
     */
    public static isiOSSafari(): boolean {
        const ua = navigator.userAgent;

        const isiOS =
            /iPhone|iPad|iPod/i.test(ua) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

        if (!isiOS) return false;

        // Exclude non-Safari iOS browsers
        const isNonSafari =
            /CriOS/i.test(ua) ||   // Chrome
            /FxiOS/i.test(ua) ||   // Firefox
            /Edg/i.test(ua) ||     // Edge
            /OPiOS/i.test(ua);     // Opera

        return !isNonSafari;
    }

    /**
     * Determines whether the current browser is running on iOS **but is NOT Safari**.
     *
     * This method detects iOS devices (iPhone, iPad, iPod, including iPadOS 13+)
     * and then checks for known non-Safari browser identifiers that still rely
     * on WebKit on iOS (e.g. Chrome, Firefox, Edge, Opera).
     *
     * Safari on iOS is explicitly excluded.
     *
     * @returns {boolean}
     * - `true` if the browser is on iOS and is **not Safari**
     * - `false` otherwise
     */
    public static isiOSNonSafari(): boolean {
        const ua = navigator.userAgent;

        const isiOS =
            /iPhone|iPad|iPod/i.test(ua) ||
            (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);

        if (!isiOS) return false;

        // iOS non-Safari browsers all identify themselves
        return (
            /CriOS/i.test(ua) ||   // Chrome
            /FxiOS/i.test(ua) ||   // Firefox
            /Edg/i.test(ua) ||     // Edge
            /OPiOS/i.test(ua)      // Opera
        );
    }

    /**
     * Checks whether the current platform is Android.
     *
     * @param {AppPlatform} [platform] - Optional platform override.
     * @returns {boolean} True if the platform is Android, otherwise false.
     */
    public static isAndroid(platform?: AppPlatform): boolean {
        if (platform === AppPlatform.ANDROID)
            return true;

        return /Android/i.test(window.navigator.userAgent);
    }

    public static deepClone(obj: any): any {
        return JSON.parse(JSON.stringify(obj));
    }

    /**
    * Compares two dates without considering the time.
    * 
    * @param {Date} date1 - First date to compare.
    * @param {Date} date2 - Second date to compare.
    * @returns {number} - Returns 0 if the dates are equal, -1 if date1 is before date2, and 1 if date1 is after date2.
    */
    public static compareDates(date1: Date, date2: Date): number {
        const d1 = new Date(date1);
        d1.setHours(0, 0, 0, 0);

        const d2 = new Date(date2);
        d2.setHours(0, 0, 0, 0);

        if (d1.getTime() === d2.getTime())
            return 0;
        else if (d1 < d2)
            return -1;
        else
            return 1;
    }

    public static formatAddressObject(address: IAddressFormat, countries: IDataCountry[]): IFormattedAddress {
        const { isoCode, countryName } = Util.getCountryInfo(address, countries);

        let fullAddress: IFormattedAddress;

        switch (isoCode) {
            case "MX":
                fullAddress = Util.formatMexicoAddress(address, countryName);
                break;
            case "US":
            default:
                fullAddress = Util.formatUSAddress(address, countryName);
                break;
        }

        Util.cleanTrailingCommas(fullAddress);
        return fullAddress;
    }

    private static createEmptyFormattedAddress(): IFormattedAddress {
        return {
            address1: "",
            address2: "",
            address3: "",
            cityState: "",
            country: ""
        };
    }

    private static getCountryInfo(address: IAddressFormat, countries: IDataCountry[]): { isoCode: string; countryName: string } {
        const country = countries.find(x => x.CountryID == address.CountryID);
        return {
            isoCode: country?.IsoCode || "",
            countryName: country?.Name || ""
        };
    }

    private static formatMexicoAddress(address: IAddressFormat, countryName: string): IFormattedAddress {
        const fullAddress = Util.createEmptyFormattedAddress();

        fullAddress.address1 += address.Address1 ? `${address.Address1}` : "";
        fullAddress.address1 += address.StreetNo ? ` ${address.StreetNo},` : "";

        if (address.Address2) {
            fullAddress.address2 += Util.formatAddress2(address.AddressTypeID, address.Address2);
        }

        fullAddress.address3 += address.Address3 ? ` ${address.Address3},` : ""; // Cologne/Neighborhood/Quarter
        fullAddress.address3 += address.Address4 ? ` ${address.Address4},` : ""; // Municipality
        fullAddress.cityState += address.ZipCode ? ` ${address.ZipCode}` : "";
        fullAddress.cityState += address.City ? ` ${address.City},` : "";
        fullAddress.cityState += address.State ? ` ${address.State},` : "";

        fullAddress.country += countryName ? ` ${countryName}` : "";

        return fullAddress;
    }

    private static formatAddress2(addressTypeID: number, address2: string): string {
        switch (addressTypeID) {
            case 1:
            case 3:
                return ` #${address2},`;
            case 4:
                return ` Suite ${address2},`;
            default:
                return "";
        }
    }

    private static formatUSAddress(address: IAddressFormat, countryName: string): IFormattedAddress {
        const fullAddress = Util.createEmptyFormattedAddress();

        fullAddress.address1 += address.StreetNo ? `${address.StreetNo} ` : "";
        fullAddress.address1 += address.Address1 ? `${address.Address1},` : "";

        if (address.Address2) {
            fullAddress.address2 += Util.formatAddress2(address.AddressTypeID, address.Address2);
        }

        fullAddress.cityState += address.City ? ` ${address.City},` : "";
        fullAddress.cityState += address.State ? ` ${address.State} ` : "";
        fullAddress.cityState += address.ZipCode ? `${address.ZipCode},` : "";

        fullAddress.country += countryName ? ` ${countryName}` : "";

        return fullAddress;
    }

    private static cleanTrailingCommas(fullAddress: IFormattedAddress): void {
        fullAddress.address1 = fullAddress.address1.replace(/,\s*$/, "");
        fullAddress.address2 = fullAddress.address2.replace(/,\s*$/, "");
        fullAddress.address3 = fullAddress.address3.replace(/,\s*$/, "");
        fullAddress.cityState = fullAddress.cityState.replace(/,\s*$/, "");
    }

    /**
    * Finds the default culture code for a country.
    * 
    * @param {string} isoCode - The iso code to search with.
    * @param {IDataCountry[]} countries - The country list to search within.
    * @returns {string} - The default culutre code.
    */
    public static getDefaultCultureCode(countryId: number, countries: IDataCountry[]): string {
        const country = countries.find(country => country.CountryID === countryId);
        return country ? country.DefaultCultureCode : 'en-us';
    }

    /**
     * Gets the value of a cookie by name.
     *
     * @param {string} name - The name of the cookie to retrieve.
     * @returns {string | null} The decoded cookie value if found; otherwise, null.
     */
    public static getCookie(name: string): string | null {
        try {
            const cookies = document.cookie.split(";");

            for (const cookie of cookies) {
                const [key, value] = cookie.split("=");
                if (key?.trim() === name)
                    return value ? decodeURIComponent(value) : "";
            }
        }
        catch {
            // do nothing
        }
        return null;
    }

    /**
     * Retrieves user location data from a cookie, if available.
     * 
     * @returns An object containing latitude and longitude if location data exists, otherwise null.
     */
    public static getUserLocationFromCookie(): { lat: number; lng: number } | null {
        const cookieLocation = Util.getCookie('locationData');
        return cookieLocation ? JSON.parse(cookieLocation) : null;
    }

    /**
     * Retrieves the user's geolocation and stores it in a cookie.
     * If location permissions are denied or unavailable, it sets default values for location.
     * @returns {Promise<void>} Resolves when location data is successfully retrieved or a default value is set.
     */
    public static async getDesktopLocationAsync(): Promise<void> {
        const cookieLocation = Util.getUserLocationFromCookie();
        if (cookieLocation)
            return;

        try {
            const permissionStatus = await navigator.permissions?.query({ name: 'geolocation' });
            if (permissionStatus.state === 'granted') {
                const position = await Util.getCurrentPositionAsync();
                if (position)
                    Util.setLocationCookie(position.latitude, position.longitude, 3600);
                else
                    Util.setLocationCookie(0, 0, -10);
            }
        } catch (error) {
            console.error('Error while checking geolocation permissions:', error);
        }
    }

    public static getCurrentPositionAsync(timeoutMs:number = 5000): Promise<SimpleCoords | null> {
        // Share the same request if one is already running, regardless of platform.
        // Several pages/components (Locations, Landing, Favorites, TrackOrder, Curbside,
        // StoreSelector) can independently ask for the current position around the same
        // time (e.g. a page's initial render racing with a BRAND_DATA_RETRIEVED-triggered
        // re-render). Without this guard, that fires a second concurrent call to
        // navigator.geolocation.getCurrentPosition() while the first is still pending,
        // which some browsers resolve as a separate permission request - causing the
        // "Allow this time" prompt to reappear a few seconds after the user already
        // answered it once.
        if (this._coordsInFlight) {
            return this._coordsInFlight;
        }

        // Native (MAUI) path
        if (this.isAppView() && typeof getMobileAppLocation === "function") {
            const nativeGetLocation = getMobileAppLocation;

            this._coordsInFlight = awaitNativeOnce<SimpleCoords | null>({
                name: "NativeLocation",
                debug: false, // set true while debugging
                setResolver: (r) => { globalThis.pendingLocationResolve = r; },
                clearResolver: () => { globalThis.pendingLocationResolve = null; },
                invokeNative: () => nativeGetLocation(),
                timeoutValue: null,
                timeoutMs,
            }).finally(() => {
                setTimeout(() => { this._coordsInFlight = null; }, 0);
            });

            return this._coordsInFlight;
        }

        this._coordsInFlight = new Promise<SimpleCoords | null>((resolve) => {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    resolve({
                        latitude: position.coords.latitude,
                        longitude: position.coords.longitude,
                    }); }, // Location services are enabled
                () => { resolve(null); } // Location services are disabled
            );
        }).finally(() => {
            setTimeout(() => { this._coordsInFlight = null; }, 0);
        });

        return this._coordsInFlight;
    }

    /**
     * Sets the location data in a cookie with a specified expiration time.
     * @param {number} lat - Latitude value.
     * @param {number} lng - Longitude value.
     * @param {number} maxAge - The maximum age of the cookie in seconds. Use negative value to expire the cookie.
     */
    public static setLocationCookie(lat: number, lng: number, maxAge: number): void {
        try {
            const domain = window.location.hostname.split('.').reverse().splice(0, 2).reverse().join('.');
            document.cookie = `locationData={"lat":${lat},"lng":${lng}};max-age=${maxAge};path=/;domain=${domain}`;
        } catch (error) {
            console.error('Error while setting location cookie:', error);
        }
    }

    /** 
     * Calculates the distance between two geographic points using the Haversine formula.
     * @param {number} lat1 - Latitude of the first point.
     * @param {number} lon1 - Longitude of the first point.
     * @param {number} lat2 - Latitude of the second point.
     * @param {number} lon2 - Longitude of the second point.
     * @returns {number} The distance between the two points in kilometers.
     */
    public static calculateDistanceByLatLong = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
        const toRadians = (degrees: number) => degrees * (Math.PI / 180);

        const R = 6371; // Radius of Earth in kilometers
        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) * Math.sin(dLat / 2) +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) * Math.sin(dLon / 2);

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c; // Distance in kilometers
    };

    /**
     * Gets the current store-local time, adjusted using the provided time offset.
     *
     * @param {Data} data - The data object containing store properties.
     * @returns {Date} A Date object representing the current store-local time.
     */
    public static getNowStore = (data: Data): Date => {
        const timeOffset = data.getProperty("TimeOffset");
        return Util.NowStore(timeOffset);
    }

    /**
     * Gets the "store minutes" for the current time.
     * Adds 1440 minutes (one day) if the current time belongs
     * to the next business date.
     *
     * @param {Data} data - The data object containing settings.
     * @returns {number} The store minutes.
     */
    public static getNowStoreMinutes(data: Data): number {
        const now = Util.getNowStore(data);

        const currentBusinessDate = Util.getBusinessDate(data);
        const nowBusinessDate = Util.getBusinessDate(data, now);

        const minutes = Util.getMinutesOfDay(now);

        if (currentBusinessDate.getTime() === nowBusinessDate.getTime() && Util.toDateOnly(now).getTime() > Util.toDateOnly(currentBusinessDate).getTime())
            return minutes + 1440;

        return minutes;
    }

    public static getNowStoreMinutesManual(timeOffset: number, businessStartMinute: number): number {
        const now = Util.NowStore(timeOffset);

        const currentBusinessDate = Util.getBusinessDateManual(timeOffset, businessStartMinute);
        const nowBusinessDate = Util.getBusinessDateManual(timeOffset, businessStartMinute, now);

        const minutes = Util.getMinutesOfDay(now);

        if (currentBusinessDate.getTime() === nowBusinessDate.getTime() && Util.toDateOnly(now).getTime() > Util.toDateOnly(currentBusinessDate).getTime())
            return minutes + 1440;

        return minutes;
    }

    public static toDateOnly(d: Date): Date {
        return new Date(d.getFullYear(), d.getMonth(), d.getDate());
    }

    /**
     * Gets the business date for a given date.
     * @param {Data} data - The data object containing settings.
     * @param {Date} [date=Util.getNowStore(data)] - The date to convert, defaults to "now".
     * @returns {Date} Business date adjusted for store's offset and start minute.
     */
    public static getBusinessDate(data: Data, date?: Date): Date {
        const timeOffset = data.getProperty("TimeOffset");
        const businessStartMinute = data.getProperty("Settings").BSM;
        const selectedDate = date ?? Util.getNowStore(data);
        return new Date(Util.GetBUSDate(selectedDate, timeOffset, businessStartMinute));
    }

    public static getBusinessDateManual(timeOffset: number, businessStartMinute: number, date?: Date): Date {
        const selectedDate = date ?? Util.NowStore(timeOffset);
        return new Date(Util.GetBUSDate(selectedDate, timeOffset, businessStartMinute));
    }

    /**
     * Gets the number of minutes since midnight for a given date.
     * @param {Date} date - The date to calculate from.
     * @returns {number} Minutes since midnight.
     */
    private static getMinutesOfDay(date: Date): number {
        return date.getHours() * 60 + date.getMinutes();
    }

    public static getTodayAndSelectedDateTimes = (data: Data, orderTypeSubType: IDataOrderTypeSubType, date: Date, minute: number): { today: IDateStartEndTime, selectedDay: IDateStartEndTime } => {
        const nowStore = Util.getNowStore(data);
        const selectedDate = Util.GetDate_StartTime_EndTime(data, date, minute, false, orderTypeSubType);
        const today = (selectedDate.IsToday) ? selectedDate : Util.GetDate_StartTime_EndTime(data, nowStore, 0, false, orderTypeSubType);
        return {
            today: today,
            selectedDay: selectedDate
        };
    }

    public static getNextOpenDay = (data: Data, orderTypeSubType?: IDataOrderTypeSubType | null): IDateStartEndTime => {
        let takingOnlineOrder = false;
        let busDate = Util.getBusinessDate(data);
        const nowMins = Util.getNowStoreMinutes(data);

        let DSTET: IDateStartEndTime = {
            IsToday: false,
            StartOrderMinute: 0,
            EndOrderMinute: 0,
            OpenMinute: 0,
            CloseMinute: 0,
            IsClosedHoliday: false,
            Online: false,
            ASAP: false,
            Date: Util.getNowStore(data),
            Minute: 0,
            IsClosedNow: false,
            HolidayID: 0,
            ThrottleInfo: null,
            FutureOrder: false,
            Paused: false
        };

        while (!takingOnlineOrder) {
            DSTET = Util.GetDate_StartTime_EndTime(data, busDate, nowMins, false, orderTypeSubType);
            takingOnlineOrder = DSTET.Online
            if (!takingOnlineOrder)
                busDate = Util.DateAdd(busDate, 1, "d");
        }
        return DSTET;
    }

    private static handleHoliday(DSTET: IDateStartEndTime, holiday: IDataHoliday, waitTime: number): void {
        DSTET.HolidayID = holiday.HolidayId;
        switch (holiday.HolidayStatusId) {
            case 0:
                // do nothing is holiday but store is open
                DSTET.ThrottleInfo = holiday.Info;
                break;
            case 1:
                // closed
                DSTET.Online = false;
                DSTET.IsClosedHoliday = true;
                DSTET.StartOrderMinute = 0;
                DSTET.EndOrderMinute = 0;
                DSTET.OpenMinute = 0;
                DSTET.CloseMinute = 0;
                break;
            case 2:
                // Holiday hours
                DSTET.StartOrderMinute = holiday.StartMinute + waitTime;
                DSTET.EndOrderMinute = holiday.EndMinute;
                DSTET.OpenMinute = holiday.StartMinute;
                DSTET.CloseMinute = holiday.EndMinute;
                DSTET.ThrottleInfo = holiday.Info;
                break;
            case 3:
                // open limited online times
                DSTET.StartOrderMinute = holiday.StartMinute + waitTime;
                DSTET.EndOrderMinute = holiday.EndMinute;
                DSTET.ThrottleInfo = holiday.Info;
                break;
            case 4:
                // Open no online ordering
                DSTET.Online = false;
                DSTET.StartOrderMinute = 0;
                DSTET.EndOrderMinute = 0;
                break;
            case 5:
                // Open no online Time/Future ordering
                DSTET.FutureOrder = false;
        }
    }

    private static handlePausedLimited(DSTET: IDateStartEndTime, otst: IDataOrderTypeSubType, nowMinute: number, now: Date, busDate: Date): boolean {
        let paused_limited = false;
        if (otst.StartMinute != 0 || otst.EndMinute != 1440) {
            DSTET.StartOrderMinute = Math.max(DSTET.StartOrderMinute, otst.StartMinute);
            DSTET.EndOrderMinute = Math.min(DSTET.EndOrderMinute, otst.EndMinute);
            paused_limited = nowMinute < DSTET.StartOrderMinute || nowMinute > DSTET.EndOrderMinute;
        }
        if (otst.PausedUntil != null && Util.DateDiff("s", new Date(otst.PausedUntil), now) > 0) {
            const pausedTime = new Date(otst.PausedUntil);
            if (pausedTime > now) {
                const dateDiff = Util.DateDiff("d", pausedTime, busDate);
                paused_limited = dateDiff >= 0;
                if (dateDiff == 0)
                    DSTET.StartOrderMinute = Math.max(DSTET.StartOrderMinute, pausedTime.getMinutes() + (60 * pausedTime.getHours()));
                else if (dateDiff > 0) {
                    DSTET.Paused = true;
                    DSTET.Online = false;
                    DSTET.StartOrderMinute = 0;
                    DSTET.EndOrderMinute = 0;
                }
            }
        }
        return paused_limited;
    }

    public static GetDate_StartTime_EndTime(data: Data, busDate: Date, busMinute: number, displayHolidayMsg: boolean, otst?: IDataOrderTypeSubType | null): IDateStartEndTime {
        const timeOffset = data.getProperty("TimeOffset");
        const businessStartMinute = data.getProperty("Settings").BSM;
        const hours = data.getProperty("Hours");

        busDate = Util.GetBUSDate(busDate, timeOffset, businessStartMinute);
        const day = busDate.getDay();
        const now = Util.NowStore(timeOffset);
        const nowMinute = Util.DateDiff("m", now, busDate) + businessStartMinute;
        const isToday = Util.DateDiff("d", busDate, Util.GetBUSDate(now, timeOffset, businessStartMinute)) == 0;
        const waitTime = data.getProperty("Settings").OOS.excludeWaitTimeFromHours ? 0 : (otst?.WaitTime ?? 0);
        const DSTET: IDateStartEndTime = {
            IsToday: isToday,
            StartOrderMinute: hours[day] ? hours[day].OrderTakingStartDayMinute + waitTime : 0,
            EndOrderMinute: hours[day]?.OrderTakingEndDayMinute || 0,
            OpenMinute: hours[day]?.OpenDayMinute || 0,
            CloseMinute: hours[day]?.CloseDayMinute || 0,
            IsClosedHoliday: false,
            Online: true,
            ASAP: true,
            Date: busDate,
            Minute: 0,
            IsClosedNow: false,
            HolidayID: 0,
            ThrottleInfo: null,
            FutureOrder: !!otst ? otst.FutureOrders : true,
            Paused: false
        };

        const holiday = Util.CheckHolidays(data, busDate, displayHolidayMsg, data.getProperty("StoreInfo").storeId);
        if (holiday) {
            this.handleHoliday(DSTET, holiday, waitTime);
        }

        const paused_limited = otst ? this.handlePausedLimited(DSTET, otst, nowMinute, now, busDate) : false;

        if (isToday)
            DSTET.StartOrderMinute = Math.max(DSTET.StartOrderMinute, nowMinute + waitTime);

        DSTET.EndOrderMinute = Math.min(DSTET.EndOrderMinute, DSTET.CloseMinute);
        DSTET.StartOrderMinute = Math.max(DSTET.StartOrderMinute, DSTET.OpenMinute);
        DSTET.StartOrderMinute = Math.min(DSTET.StartOrderMinute, DSTET.EndOrderMinute);

        DSTET.IsClosedNow = DSTET.IsClosedNow || nowMinute < DSTET.OpenMinute || nowMinute > DSTET.CloseMinute;

        DSTET.ASAP = !DSTET.IsClosedNow && !paused_limited;

        DSTET.StartOrderMinute = Math.ceil(DSTET.StartOrderMinute / 5) * 5;
        DSTET.EndOrderMinute = Math.ceil(DSTET.EndOrderMinute / 5) * 5;
        DSTET.Minute = Math.max(busMinute, DSTET.StartOrderMinute);
        return DSTET;
    }

    public static CheckHolidays(data: Data, date: Date, displayHolidayMessage: boolean, selectedCallCenterStoreId: number): IDataHoliday | null {
        const storeBusinessDate = data.getProperty("StoreBusinessDate");
        const timeOffset = data.getProperty("TimeOffset");

        const holidayId = Util.IsHoliday(date, storeBusinessDate, timeOffset);
        const holiday = Util.getHolidayById(data, holidayId, selectedCallCenterStoreId);
        if (!holiday || holiday?.HolidayId === 0)
            return null;

        return holiday;
    }

    public static getHolidayById(data: Data, holidayId: number, selectedStoreId: number): IDataHoliday | null {
        let holidays = data.getProperty("Holidays");
        if (data.getProperty("Settings").ISCC && selectedStoreId !== 0) {
            const store = Util.getCallCenterStore(selectedStoreId, data.getProperty("Stores"));
            if (store)
                holidays = store.Holidays;
        }

        if (holidayId > 0 && holidays)
            return holidays.find(holiday => holiday.HolidayId == holidayId) || null;
        return null;
    }

    public static getCallCenterStore(storeID: number, stores: IDataStore[]): IDataStore | null {
        return stores.find(store => store.StoreId === storeID) || null;
    }

    /**
    * Utility to format date as "Month Name, Day Number, Year"
    */
    public static formatDateWithOptions(dateString: string, cultureCode: string, options: Intl.DateTimeFormatOptions): string {
        const tempDate = dateString.includes("-") ? Util.formatDateFunc(dateString) : dateString;
        const date = new Date(tempDate);
        const cc = cultureCode ?? "en-US";
        return date.toLocaleDateString(cc, options);
    }

    /**
     * Appends the correct ordinal indicator to a given number.
     * @param {number} number - The number to which the ordinal indicator will be appended.
     * @returns {string} The number with the correct ordinal indicator.
     */
    public static GetOrdinalIndicator = (number: number): string => {
        const j = number % 10,
            k = number % 100;
        if (j == 1 && k != 11) {
            return number + "st";
        }
        if (j == 2 && k != 12) {
            return number + "nd";
        }
        if (j == 3 && k != 13) {
            return number + "rd";
        }
        return number + "th";
    }

    /**
     * Scrolls to the first invalid input or checkbox within a given parent container.
     * Ensures smooth scrolling and accounts for fixed headers if applicable.
     *
     * @param {string} parentId - The ID of the parent container where validation errors may exist.
     */
    public static scrollToFirstInvalidElement(parentId: string): void {
        const parent = document.getElementById(parentId);
        if (!parent)
            return;

        // Find the first invalid input or checkbox
        const invalidElement = parent.querySelector(".invalid-input, .invalid-checkbox");
        if (!invalidElement)
            return;

        // Determine if scrolling should occur within <main> instead of document body
        const isMainScroll = (parent.parentNode as HTMLElement | null)?.tagName.toLowerCase() === "main";

        // Offset adjustment if a fixed header exists
        let offset = 0;
        if (isMainScroll) {
            const header = document.querySelector(".header") as HTMLElement | undefined;
            offset = header ? header.offsetHeight : 0;
        }

        // Get element position relative to the viewport
        const elementRect = invalidElement.getBoundingClientRect();

        // Calculate target scroll position to center element in viewport
        const viewportHeight = window.innerHeight;
        let targetScroll = isMainScroll
            ? document.body.scrollTop + elementRect.top - offset - viewportHeight / 2 + elementRect.height / 2 // Center in viewport
            : parent.scrollTop + elementRect.top - parent.clientHeight / 2 + elementRect.height / 2; // Center inside modal

        // Ensure target scroll is not negative
        targetScroll = Math.max(targetScroll, 0);

        // Perform smooth scrolling
        const scrollContainer = isMainScroll ? document.body : parent;
        scrollContainer.scrollTo({ top: targetScroll, behavior: "smooth" });
    }

    /**
     * Hides validation error messages and removes invalid styling from an input element.
     *
     * @param {string} inputId - The ID of the input field or checkbox to clear validation errors from.
     * @param {string} errorId - The ID of the error message element to hide.
     */
    public static hideElementErrors(inputId: string, errorId: string): void {
        // Hide the associated error message element
        Util.hideElement(errorId);

        const input = document.getElementById(inputId) as HTMLInputElement || null;
        if (!input)
            return;

        const isCheckbox = (input.type === "checkbox");

        if (isCheckbox) {
            // Remove invalid checkbox styling from the associated label
            const label = document.querySelector(`label[for="${inputId}"]`) as HTMLLabelElement | undefined;
            label?.classList.remove("invalid-checkbox");
        } else {
            // Remove invalid input styling
            Util.setElementClass("remove", inputId, "invalid-input");
        }
        input.removeAttribute("aria-describedby");
    }

    /**
     * Scrolls to an element by its ID.
     * If the element does not exist immediately, it optionally waits for a given time before attempting to scroll.
     * 
     * @param id - The ID of the element to scroll to.
     * @param retries - (Optional) Number of retries to find the element if it does not exist initially. Default is 0.
     * @param ms - (Optional) Time in milliseconds to wait before attempting to scroll. Default is 0 (immediate scroll).
     * @param initDelay - (Optional) Initial delay in milliseconds. Default is 0.
     * @param behavior - (Optional) Scroll behavior. Default is "smooth".
     */
    public static scrollToElementId(
        id: string,
        retries: number = 0,
        ms: number = 0,
        initDelay: number = 0,
        behavior: ScrollBehavior = "smooth",
        options?: {
            scrollContainerSelector?: string;
            stickyHeaderSelector?: string;
        }
    ): void {
        const checkExist = (attempts: number) => {
            const element = document.getElementById(id);
            if (!element) {
                if (attempts > 0) {
                    setTimeout(() => checkExist(attempts - 1), ms);
                }
                return;
            }

            const scrollContainer = options?.scrollContainerSelector
                ? element.closest(options.scrollContainerSelector)
                : null;

            // Default behavior (unchanged)
            if (!scrollContainer) {
                element.scrollIntoView({ behavior, block: "start" });
                return;
            }

            const stickyHeader = options?.stickyHeaderSelector
                ? (scrollContainer.querySelector(options.stickyHeaderSelector) as HTMLElement | null)
                : null;

            const headerHeight = stickyHeader?.offsetHeight ?? 0;

            const elementTop =
                element.getBoundingClientRect().top -
                scrollContainer.getBoundingClientRect().top;

            const targetScrollTop =
                elementTop + scrollContainer.scrollTop - headerHeight;

            scrollContainer.scrollTo({
                top: targetScrollTop,
                behavior
            });
        };

        setTimeout(() => checkExist(retries), initDelay);
    }

    /**
     * Debounces a function to limit the rate at which it can be called.
     * @param {Function} func - The function to debounce.
     * @param {number} wait - The number of milliseconds to wait before allowing the function to be called again.
     * @param {boolean} leading  - If true, the first call takes precedence (leading debounce). 
     *                           - If false, the last call is executed after the wait period (trailing debounce).
     */
    public static Debounce<T extends (...args: any[]) => void>(func: T, wait: number, leading: boolean = false) {
        let timeout: ReturnType<typeof setTimeout> | null = null;
        let isRunning = false;
        let lastArgs: Parameters<T> | null = null;

        return function executedFunction(this: any, ...args: Parameters<T>) {
            if (leading) {
                if (isRunning) return; //leading debounce, ignore subsequent calls until wait time is over

                isRunning = true;
                timeout = setTimeout(() => {
                    isRunning = false;
                }, wait);

                func.apply(this, args);
            } else { // Trailing debounce, last call will be executed after wait
                lastArgs = args;
                clearTimeout(timeout!);
                timeout = setTimeout(() => {
                    func.apply(this, lastArgs!);
                    lastArgs = null;
                }, wait);
            }
        };
    }

    public static generateHex = (size: number) => [...Array(size)].map(() => Math.floor(Math.random() * 16).toString(16)).join('');

    /**
     * Determines if current location is Bahamas
     * @returns {boolean} True if the current location is Bahamas, false otherwise.
     */
    public static isBahamas(longitude: number, latitude: number): boolean {
        return latitude == BahamasCoordinates.LAT && longitude == BahamasCoordinates.LONG;
    }

    /**
     * Validates a credit card number using the Luhn algorithm and updates card brand icons.
     *
     * @param {string} cNumber - The credit card number to validate.
     * @returns {number} A positive card brand value if valid, a negative one if invalid, or -100 if unknown brand.
     */
    public static validateCardNumber(cNumber: string): number {
        const cardBrand = Util.getCardBrand(cNumber);
        const length = cNumber.length;

        // Unknown card brand after typing enough digits
        if (cardBrand === CardBrand.UNKNOWN && length > 3)
            return -100;

        // Define expected card lengths
        const validLengths: Record<number, number[]> = {
            [CardBrand.VISA]: [16],
            [CardBrand.MASTERCARD]: [16],
            [CardBrand.AMEX]: [15],
            [CardBrand.DISCOVER]: [16],
            [CardBrand.DINERSCLUB]: [14, 16],
            [CardBrand.JCB]: [16]
        };

        const isLengthValid = validLengths[cardBrand]?.includes(length);
        if (!isLengthValid)
            return 0;

        // Run Luhn check
        const isValid = Util.luhnCheck(cNumber);
        return isValid ? cardBrand : -cardBrand;
    }

    /**
     * Performs Luhn algorithm to validate a card number.
     *
     * @param {string} cardNumber - The credit card number to check.
     * @returns {boolean} True if the card number is valid per Luhn algorithm.
     */
    public static luhnCheck(cardNumber: string): boolean {
        let sum = 0;
        let shouldDouble = false;

        for (let i = cardNumber.length - 1; i >= 0; i--) {
            let digit = parseInt(cardNumber[i], 10);
            if (shouldDouble) {
                digit *= 2;
                if (digit > 9) digit -= 9;
            }
            sum += digit;
            shouldDouble = !shouldDouble;
        }

        return sum % 10 === 0;
    }

    public static getCardBrand(cNumber: string): CardBrand {
        switch (true) {
            case /^3[47]/.test(cNumber):
                return CardBrand.AMEX;
            case /^4/.test(cNumber):
                return CardBrand.VISA;
            case /^5[1-5]|^2[2-7]/.test(cNumber):
                return CardBrand.MASTERCARD;
            case /^6(?:011|5|44|49)/.test(cNumber):
                return CardBrand.DISCOVER;
            case /^3(?:0[0-5]|[68]|[39])/.test(cNumber):
                return CardBrand.DINERSCLUB;
            case /^(?:2131|1800|35)/.test(cNumber):
                return CardBrand.JCB;
            default:
                return CardBrand.UNKNOWN;
        }
    }

    public static GetCardName(cardNumber: string): string {
        const cardType = Util.getCardBrand(cardNumber);

        switch (cardType) {
            case CardBrand.VISA:
                return "Visa";
            case CardBrand.MASTERCARD:
                return "Mastercard";
            case CardBrand.AMEX:
                return "American Express";
            case CardBrand.DISCOVER:
                return "Discover";
            case CardBrand.DINERSCLUB:
                return "Diners Club";
            case CardBrand.JCB:
                return "JCB";
            case CardBrand.DINERS:
                return "Diners";
            default:
                return "";
        }
    }

    /**
     * Maps the payment processor's raw CLOGO value (see TriPOSCreditCard.GetAdoraCreditCardType --
     * "visa"/"mastercard"/"amex"/"discover"/"diners"/"jcb"/"interac", case-insensitive) to the same
     * display names GetCardName produces. Used for Apple Pay/Google Pay, where TransData carries
     * CLOGO from the processor response instead of a raw card number to run BIN detection on.
     */
    public static GetCardNameFromLogo(logo: string): string {
        switch ((logo || "").toLowerCase()) {
            case "visa":
                return "Visa";
            case "mastercard":
                return "Mastercard";
            case "amex":
                return "American Express";
            case "discover":
                return "Discover";
            case "diners":
                return "Diners Club";
            case "jcb":
                return "JCB";
            case "interac":
                return "Interac";
            default:
                return "";
        }
    }

    /**
     * Resolves the card brand name and last-4 to display for a CREDIT_CARD payment (credit card,
     * Apple Pay, or Google Pay -- TransData is populated the same way for all of them, see
     * BuildPaymentsTransData in OrderService.cs). Checks CNO first (present when the client submits
     * a raw card number), then WLST4 (Apple Pay only -- the wallet's own DPAN last4, decrypted from
     * the token, which can differ from WLST4's sibling LST4 below since that one is WorldPay's own
     * reported last4), then LST4/CLOGO (the post-charge processor response merged in by
     * checkout.ts), falling back to payment.AccountID (last 4 only, no logo) if TransData isn't
     * parseable.
     */
    public static ResolveCreditCardDisplay(payment: IOrderPayment): { last4: string, cardName: string } {
        try {
            const transactionData = JSON.parse(payment.TransData);
            if (transactionData.CNO)
                return { last4: transactionData.CNO.slice(-4), cardName: Util.GetCardName(transactionData.CNO) };
            if (transactionData.WLST4)
                return { last4: transactionData.WLST4, cardName: Util.GetCardNameFromLogo(transactionData.CLOGO) };
            if (transactionData.LST4)
                return { last4: transactionData.LST4, cardName: Util.GetCardNameFromLogo(transactionData.CLOGO) };
        } catch { /* fall back to payment.AccountID below */ }

        return { last4: payment.AccountID.slice(-4), cardName: Util.GetCardName(payment.AccountID) };
    }

    /**
     * Sets a cookie with a specified name, value, and expiration in days.
     * @param name - The name of the cookie.
     * @param value - The value of the cookie.
     * @param days - The number of days until the cookie expires.
     */
    public static setCookie(name: string, value: string, days: number, domain?: string): void {
        let domainStr = '';
        if (domain)
            domainStr = `domain=${domain}`;
        const expires = new Date(Date.now() + days * 864e5).toUTCString(); // 864e5 = 1000*60*60*24
        document.cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; expires=${expires}; path=/; ${domainStr}`;
    }

    /**
     * Deletes a cookie by setting its expiration date to a past date.
     * @param name - The name of the cookie to delete.
     */
    public static deleteCookie(name: string, domain?: string): void {
        let domainStr = '';
        if (domain)
            domainStr = `domain=${domain}`;
        document.cookie = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; ${domainStr}`;
    }

    /**
     * Sets the consent for Google Tag Manager and saves the consent information on  
     * localStorage when the user explicitly chooses a cookie preference.
     * Note: Consent is denied for all types by default upon loading the google tag manager on page load. 
     * @returns
     */
    public static setConsent(isGranted: boolean): void {
        //@ts-expect-error - ambient declaration for gtag
        if (!globalThis.gtag)
            return;

        const consentResult = isGranted ? 'granted' : 'denied';

        const consentMode = {
            'ad_storage': consentResult,
            'analytics_storage': consentResult,
            'personalization_storage': consentResult,
            'functionality_storage': consentResult,
            'security_storage': consentResult,
        }

        //@ts-expect-error - ambient declaration for gtag
        globalThis.gtag('consent', 'update', consentMode);
        localStorage.setItem('consentMode', JSON.stringify(consentMode));
    }

    /**
     * Gets the number of days until a cookie expires.
     * @param expires - The expiration date of the cookie in string format.
     * @returns {number} - The number of days until the cookie expires.
     */
    public static getDaysUntilExpiry(expires: string): number {
        const now = new Date();
        const expiryDate = new Date(expires);

        // Calculate difference in milliseconds
        const diffInMs = expiryDate.getTime() - now.getTime();

        // Convert to days
        const diffInDays = Math.ceil(diffInMs / (1000 * 60 * 60 * 24));

        return diffInDays;
    }

    /**
     * Retrieves user's cookie preferences from a cookie, if available.
     * @returns {string} - the value of the cookie preference, either "accepts" or "rejects"
     */
    public static getCookiePreferenceFromCookie(): string | null {
        const cookiePreference = Util.getCookie('cookiePreference');
        return cookiePreference;
    }

    /**
     * Attempts to load and run a javascript file from an external url
     * @param url - The URL location of an external javascript file
     * @returns {Promise<boolean>} - True if the script loaded successfully, false otherwise.
     */
    public static async loadScriptAsync(url: string): Promise<boolean> {
        return new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = url;
            script.type = 'text/javascript';
            script.async = true;

            script.onload = () => {
                resolve(true);
            };
            script.onerror = (err) => {
                reject(err);
            };

            document.head.appendChild(script);
        });
    }

    /**
     * Builds a menu context breadcrumb by traversing the DOM hierarchy.
     * @param element - The starting element (e.g., the clicked menu tab).
     * @param tagToSearch - The tag to search for when collecting the menu specific breadcrumbs
     * @returns An array of breadcrumb items with friendly text.
     */
    public static getMenuBreadcrumb(element: HTMLElement, tagToSearch: string): string[] {
        const breadcrumb: string[] = [];
        const normalizedTag = tagToSearch.toUpperCase();

        // Traverse up the DOM tree
        let currentElement: HTMLElement | null = element;
        while (currentElement) {
            const tag = currentElement.tagName.toUpperCase();

            // 1. Check if it's a matching element tag
            if (normalizedTag === tag) {
                breadcrumb.unshift(currentElement.textContent?.trim() || "Unnamed Link");
            }

            // 2. Check if menu tabs are at this level and search for active tab
            let prevSibling = currentElement.previousElementSibling as HTMLElement | null;
            while (prevSibling) {
                if (prevSibling.id === 'div_menu_menu_tabs') {
                    const tabs = prevSibling.querySelectorAll<HTMLButtonElement>('button') || [];
                    const activeTab = Array.from(tabs).find(x => x.classList.contains("active"));
                    if (activeTab) {
                        breadcrumb.unshift(activeTab.textContent?.trim() || "Unnamed Tab")
                    }
                }
                prevSibling = prevSibling.previousElementSibling as HTMLElement | null;
            }

            // 3. Special handling for page ID marker (e.g., "page_menu")
            if (currentElement.id && currentElement.id.startsWith("page_")) {
                const cleaned = currentElement.id.replace("page_", "");
                const capitalized = this.capitalizeFirst(cleaned);
                const pageName = `${capitalized} Page`;
                breadcrumb.unshift(pageName);
                break; // Stop after hitting page container
            }

            currentElement = currentElement.parentElement;
        }

        return breadcrumb;
    }

    /**
     * Calculates the great-circle distance between two points on Earth using the Haversine formula.
     * @param lat1 - Latitude of the first point.
     * @param lon1 - Longitude of the first point.
     * @param lat2 - Latitude of the second point.
     * @param lon2 - Longitude of the second point.
     * @returns Distance in miles between the two coordinates.
     */
    public static haversineMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
        const toRadians = (deg: number) => deg * Math.PI / 180;
        const R = 3958.8; // Earth's radius in miles

        const dLat = toRadians(lat2 - lat1);
        const dLon = toRadians(lon2 - lon1);

        const a =
            Math.sin(dLat / 2) ** 2 +
            Math.cos(toRadians(lat1)) * Math.cos(toRadians(lat2)) *
            Math.sin(dLon / 2) ** 2;

        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        return R * c;
    }

    /**
     * Returns the preferred web storage method (localStorage or sessionStorage) based on cookie preferences.
     * If cookies are rejected or not set, sessionStorage is used as fallback.
     * @returns The selected `Storage` object.
     */
    public static getPreferredStorage(): Storage {
        const pref = Util.getCookiePreferenceFromCookie();
        return (!pref || pref === CookiePreferences.REJECT) ? sessionStorage : localStorage;
    }

    public static getTempAddressFromUserAddress(address: IUserAddress): ITempAddress {
        return {
            AddressID: address.AID,
            AddressTypeID: address.ATID,
            AddressName: address.ANAM ?? "",
            IsPrimary: address.IPRM,
            StreetNo: address.STRNO,
            StreetName: address.ADDR1,
            Address2: address.ADDR2,
            Address3: address.ADDR3,
            Address4: address.ADDR4,
            Address5: address.ADDR5,
            City: address.CITY,
            State: address.STA,
            Zip: address.ZIP,
            CountryID: address.CID,
            Latitude: address.LAT,
            Longitude: address.LON,
            XStreet: address.XSTR,
            ZoneID: address.ZID ?? 0,
            Grid: address.GRD ?? "",
            Distance: address.DIS ?? 0,
            DeliveryInstruction: address.DI ?? ""
        };
    }

    public static capitalizeFirst(str: string): string {
        return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
    }

    public static parseJsonWithDates<T>(jsonString: string): T {
        return JSON.parse(jsonString, (key, value) => {
            const isoDateRegex = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(Z|[+-]\d{2}:\d{2})?$/;
            if (typeof value === 'string' && isoDateRegex.test(value)) {
                return new Date(value);
            }
            return value;
        });
    }

    public static checkZones(latitude: number, longitude: number, deliveryZones: IStoreLocationZone[]): IAddressZone | null {
        if (deliveryZones.length === 0)
            return null;

        const zoneStat: IAddressZone = {
            ZTID: 0,
            ZoneID: 0,
            ZID: 0,
            SID: 0,
            MNDL: 0,
            ILT: false,
            LIMTIME: []
        };

        const point = {
            Latitude: latitude,
            Longitude: longitude
        } as IDeliveryZonePolygon;

        // First: try to find a matching zone with isLimitedTime = true
        const limitedTimeMatch = deliveryZones.find(zone =>
            zone.isLimitedTime && Util.isPointInPolygon(zone.polygons, point)
        );

        // If no limited time match, fallback to first general match
        const firstMatch = deliveryZones.find(zone =>
            Util.isPointInPolygon(zone.polygons, point)
        );

        const zone = limitedTimeMatch || firstMatch;
        if (zone) {
            zoneStat.ZTID = zone.typeId;
            zoneStat.ZID = zone.id;
            zoneStat.ZoneID = zone.id;
            zoneStat.SID = zone.storeId;
            zoneStat.MNDL = zone.minimumDeliveryAmount;
            zoneStat.ILT = zone.isLimitedTime;
            zoneStat.LIMTIME = zone.limitedTimes;
        }

        return zoneStat.ZID > 0 ? zoneStat : null;
    }

    public static isPointInPolygon(poly: IStoreLocationZonePolygon[], point: IDeliveryZonePolygon): boolean {
        let j = poly.length - 1;
        let oddNodes = false;
        for (let i = 0; i < poly.length; i++) {
            const PointIx = Util.Float12(poly[i].longitude);
            const PointIy = Util.Float12(poly[i].latitude);
            const PointJx = Util.Float12(poly[j].longitude);
            const PointJy = Util.Float12(poly[j].latitude);
            if ((((PointIy <= point.Latitude) && (point.Latitude < PointJy)) || ((PointJy <= point.Latitude) && (point.Latitude < PointIy))) &&
                (point.Longitude < (PointJx - PointIx) * (point.Latitude - PointIy) / (PointJy - PointIy) + PointIx)) {
                oddNodes = !oddNodes;
            }
            j = i;
        }
        return oddNodes;
    }

    public static attachNumericInputValidation(
        input: HTMLInputElement,
        options: {
            min?: number,
            max?: number,
            decimals?: number,
            culture?: string
        } = {}
    ): void {
        const min = options.min ?? 0;
        const max = options.max ?? Infinity;
        const decimals = options.decimals ?? 2;

        const original = (input.value).trim();
        const hadMinus = original.startsWith("-");

        // Keep only digits and dots
        let v = original.replace(/[^\d.]/g, "");

        // Allow only a single dot
        const firstDot = v.indexOf(".");
        if (firstDot !== -1) {
            v = v.slice(0, firstDot + 1) + v.slice(firstDot + 1).replace(/\./g, "");
        }

        // Limit decimal places
        const regex = new RegExp(`^(\\d+)(\\.\\d{0,${decimals}})?`);
        const m = regex.exec(v);
        if (m)
            v = m[0];

        // negatives are not allowed → clamp to min
        if (min >= 0 && hadMinus) {
            input.value = min.toFixed(decimals);
            return;
        }

        // Clamp range
        const num = parseFloat(v);
        if (!isNaN(num)) {
            if (num < min) v = min.toFixed(decimals);
            else if (num > max) v = max.toFixed(decimals);
        }

        input.value = v;
    }

    private static _srAnnounceLocked = false;
    private static _srQueue: string[] = [];
    private static _srQueueTimer: number | null = null;
    private static _srLastBatchKey: string | null = null;
    private static _announceCycle = 0;
    private static _srBatchMode = false;

    public static beginBatchAnnouncement(): void {
        Util._srBatchMode = true;
    }

    public static endBatchAnnouncement(): void {
        Util._srBatchMode = false;
    }

    public static get isBatchAnnouncementActive(): boolean {
        return Util._srBatchMode;
    }

    public static getAnnounceCycle(): number {
        return Util._announceCycle;
    }

    public static resetAnnounceCycle(): void {
        Util._srAnnounceLocked = false;
        Util._announceCycle++;
        Util._srQueue = [];
        if (Util._srQueueTimer !== null) {
            window.clearTimeout(Util._srQueueTimer);
            Util._srQueueTimer = null;
        }
        Util._srLastBatchKey = null;
    }


    public static ensureAnnouncerPresent(): void {
        Util.getOrCreateAnnouncer();
    }

    private static isElementActiveForAT(el: HTMLElement): boolean {
        const isHiddenFromAT = (node: HTMLElement): boolean =>
            node.getAttribute("aria-hidden") === "true";

        if (!el.isConnected) return false;

        for (let cur: HTMLElement | null = el; cur; cur = cur.parentElement) {
            if (isHiddenFromAT(cur)) return false;
        }

        return true;
    }

    private static getAnnouncerHost(): HTMLElement {
        const openDialog = document.querySelector<HTMLDialogElement>('dialog[open]');
        if (openDialog && Util.isElementActiveForAT(openDialog)) return openDialog;

        return document.body ?? document.documentElement;
    }

    private static isInClosedDialog(el: HTMLElement): boolean {
        const dlg = el.closest("dialog");
        return !!dlg && !dlg.hasAttribute("open");
    }

    private static getOrCreateAnnouncer(): HTMLElement {
        const host = Util.getAnnouncerHost();

        const existing = document.getElementById("sr-announcer");
        const existingRegion = existing instanceof HTMLElement ? existing : null;

        const needsNew =
            !existingRegion ||
            !existingRegion.isConnected ||
            !host.contains(existingRegion) ||
            Util.isInClosedDialog(existingRegion); // <-- key


        if (!needsNew) {
            existingRegion.removeAttribute("aria-hidden");
            existingRegion.style.display = "";
            return existingRegion;
        }

        existingRegion?.remove();

        const region = document.createElement("div");
        region.id = "sr-announcer";
        region.setAttribute("aria-live", "assertive");
        region.setAttribute("aria-atomic", "true");

        region.style.position = "absolute";
        region.style.left = "-9999px";
        region.style.width = "1px";
        region.style.height = "1px";
        region.style.overflow = "hidden";

        host.appendChild(region);
        return region;
    }

    public static announce(message: string): void {
        const msg = message.trim();
        if (!msg) return;

        Util._srQueue.push(msg);

        if (!Util._srAnnounceLocked) {
            Util._srAnnounceLocked = true;
            Util._playQueue();
        }
    }

    private static _announceNow(msg: string): void {
        const region = Util.getOrCreateAnnouncer();

        // Strong mutation so identical messages repeat
        while (region.firstChild) region.removeChild(region.firstChild);

        const item = document.createElement("div");
        item.textContent = msg;
        region.appendChild(item);

        // Remove later so next mutation is clean
        window.setTimeout(() => item.remove(), 2000);
    }

    private static _playQueue(): void {
        if (Util._srQueueTimer !== null) {
            window.clearTimeout(Util._srQueueTimer);
            Util._srQueueTimer = null;
        }

        const next = Util._srQueue.shift();
        if (!next) {
            Util._srAnnounceLocked = false;
            return;
        }

        Util._announceNow(next);

        // Delay before speaking the next item.
        // 700–1200ms tends to work well; tune if needed.
        Util._srQueueTimer = window.setTimeout(() => Util._playQueue(), 900);
    }

    public static announceErrors(errors: string[]): void {
        const cleaned = (errors || [])
            .map(e => (e || "").trim())
            .filter(Boolean);

        if (cleaned.length === 0) return;

        // Always start a fresh batch
        Util._srQueue = [];
        if (Util._srQueueTimer !== null) {
            window.clearTimeout(Util._srQueueTimer);
            Util._srQueueTimer = null;
        }
        Util._srAnnounceLocked = false;

        const countMsg = `${cleaned.length} error${cleaned.length === 1 ? "" : "s"} on the page.`;
        Util._srQueue.push(countMsg, ...cleaned);

        Util._srAnnounceLocked = true;
        Util._playQueue();
    }

    public static collectVisibleErrors(container: string | HTMLElement): string[] {
        const root =
            typeof container === "string"
                ? document.getElementById(container)
                : container;

        if (!root) return [];

        const nodes = Array.from(
            root.querySelectorAll<HTMLElement>(".error-text, [data-error], [id$='_error']")
        );

        const isVisible = (el: HTMLElement): boolean => {
            if (el.classList.contains("hidden")) return false;
            if (el.getAttribute("aria-hidden") === "true") return false;

            const s = window.getComputedStyle(el);
            return s.display !== "none" && s.visibility !== "hidden";
        };

        const cycle = Util.getAnnounceCycle();
        const msgs: string[] = [];

        for (const el of nodes) {
            if (!isVisible(el)) continue;

            const msg = (el.textContent || "").trim();
            if (!msg) continue;

            // Prevent announcing the same element multiple times *within the same run*
            const lastCycle = Number(el.getAttribute("data-last-announced-cycle") || "0");
            const lastMsg = el.getAttribute("data-last-announced-error") || "";

            if (lastCycle === cycle && lastMsg === msg) continue;

            el.setAttribute("data-last-announced-cycle", String(cycle));
            el.setAttribute("data-last-announced-error", msg);

            msgs.push(msg);
        }

        return msgs;
    }

    /**
     * Returns the localized weekday name for a given weekday ID.
     *
     * Weekday IDs follow JavaScript's `Date.getDay()` convention:
     * - 0 = Sunday
     * - 6 = Saturday
     *
     * @param {number} weekdayId - Numeric weekday identifier (0–6).
     * @returns {string} The weekday name, or an empty string if the ID is invalid.
     */
    public static getWeekdayNameById(weekdayId: number): string {
        switch (weekdayId) {
            case 0:
                return "Sunday";
            case 1:
                return "Monday";
            case 2:
                return "Tuesday";
            case 3:
                return "Wednesday";
            case 4:
                return "Thursday";
            case 5:
                return "Friday";
            case 6:
                return "Saturday";
            default:
                return "";
        }
    }

    /**
     * Opens turn-by-turn driving directions to a destination coordinate.
     *
     * Behavior by runtime:
     * - In the shell app (app webview): delegates to the native bridge function `openDirectionsToLocation`
     *   (if available) to launch the device's default maps app.
     * - In iOS browsers: opens Apple Maps with driving directions pre-filled.
     * - In other browsers/desktop: opens Google Maps (web) with driving directions pre-filled.
     *
     * @param {number} latitude - Destination latitude in decimal degrees (WGS84).
     * @param {number} longitude - Destination longitude in decimal degrees (WGS84).
     * @returns {void}
     */
    public static openDirections(latitude: number, longitude: number): void {
        const destination = encodeURIComponent(`${latitude},${longitude}`);

        if (Util.isAppView() && typeof openDirectionsToLocation == "function") {
            const payload = JSON.stringify({ latitude, longitude });
            openDirectionsToLocation(payload);
            return;
        }

        if (Util.isiOSSafari() || Util.isiOSNonSafari()) {
            // Requirement: Web on iOS -> Apple Maps
            const url = `https://maps.apple.com/?daddr=${destination}&dirflg=d`;
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }

        // Web view / desktop / non-iOS browsers -> Google Maps web
        const gmaps = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`;
        window.open(gmaps, "_blank", "noopener,noreferrer")
    }

    public static getCreditCardTypeById(cardTypeId: number): string {
        return this.creditCardTypes[cardTypeId] ?? "";
    }

    public static populateMonthSelectList(monthSelectElement: HTMLSelectElement, year: number, language: string, localizationService: LocalizationService): void {
        monthSelectElement.innerHTML = "";

        this._addDefaultOption(monthSelectElement, "", localizationService, "MarkSelMonth");

        for (let month = 1; month <= 12; month++) {
            const monthName = new Date(year, month - 1).toLocaleString(language, { month: 'short' });
            const monthNumber = month.toString().padStart(2, '0');
            const option = document.createElement("option");
            option.value = month.toString();
            option.text = `${monthNumber} - ${monthName}`;
            option.setAttribute("ltagj", this.getLtagjForMonth(year, month, localizationService));
            monthSelectElement.appendChild(option);
        }
    }

    public static getLtagjForMonth(year: number, month: number, localizationService: LocalizationService): string {
        const monthNumber = month.toString().padStart(2, '0');
        const ltagj: { lc: string, text: string }[] = [];

        localizationService.getSupportedLanguages().forEach(lang => {
            const monthName = new Date(year, month - 1).toLocaleString(lang, { month: 'short' });
            ltagj.push({ lc: lang, text: `${monthNumber} - ${monthName}` });
        });

        return encodeURI(JSON.stringify(ltagj));
    }

    public static populateDaySelectList(daySelectElement: HTMLSelectElement, year: number, month: number, localizationService: LocalizationService): void {
        daySelectElement.innerHTML = "";

        this._addDefaultOption(daySelectElement, "-1", localizationService, "MarkSelDay");

        if (month < 1 || month > 12) return;

        const daysInMonth = this.getDaysInMonth(month, year);

        for (let day = 1; day <= daysInMonth; day++) {
            const option = document.createElement("option");
            option.value = day.toString();
            option.text = day.toString();
            daySelectElement.appendChild(option);
        }
    }

    private static _addDefaultOption(
        selectElement: HTMLSelectElement,
        defaultValue: string,
        localizationService: LocalizationService,
        localizationKey: string
    ): void {
        const option = document.createElement("option");
        option.value = defaultValue;
        option.text = localizationService.names(localizationKey);

        const ltagj: { lc: string, text: string }[] = [];

        localizationService.getSupportedLanguages().forEach(lang => {
            ltagj.push({ lc: lang, text: localizationService.names(localizationKey, lang) });
        });

        option.setAttribute("ltagj", encodeURI(JSON.stringify(ltagj)));

        selectElement.appendChild(option);
    }
    /**
     * Gets the number of days in a given month and year, accounting for leap years.
     * @param month The month (1-12) for which to get the number of days.
     * @param year The year for which to get the number of days (used to determine leap years).
     * @returns The number of days in the specified month and year.
     */
    public static getDaysInMonth(month: number, year: number): number {
        return new Date(year, month, 0).getDate();
    }

    public static isValidDate(year: number, month: number, day: number): boolean {
        // month is 1-based here
        const date = new Date(year, month - 1, day);
        return (
            date.getFullYear() == year &&
            date.getMonth() == (month - 1) &&
            date.getDate() == day
        );
    }

    public static redirect(url: string): void {
        window.location.href = url;
    }


    // 2DO: Dark Mode stuff
    /**
    * Toggles the dark mode for the application and updates the colors accordingly.
    * @returns {void}
    */
    //public static toggleDarkMode(temp: ITemp, user: User): void {
    //    temp.DarkMode = !temp.DarkMode;

    //    if (temp.DarkMode)
    //        localStorage.setItem("darkMode", "true");
    //    else
    //        localStorage.removeItem('darkMode');

    //    if (user.isLoggedIn())
    //        user.updateUserDarkMode(temp.DarkMode);

    //    const root = document.documentElement;

    //    if (temp.DarkMode) {
    //        root.style.setProperty('--colorText', getComputedStyle(root).getPropertyValue('--darkColorText'));
    //        root.style.setProperty('--colorInputBg', getComputedStyle(root).getPropertyValue('--darkColorInputBg'));
    //        root.style.setProperty('--colorFocusBg', getComputedStyle(root).getPropertyValue('--darkColorFocusBg'));
    //        root.style.setProperty('--colorFocus', getComputedStyle(root).getPropertyValue('--darkColorFocus'));
    //        root.style.setProperty('--colorBg', getComputedStyle(root).getPropertyValue('--darkColorBg'));
    //        root.style.setProperty('--colorBgCard', getComputedStyle(root).getPropertyValue('--darkColorBgCard'));
    //        root.style.setProperty('--colorBgRow', getComputedStyle(root).getPropertyValue('--darkColorBgRow'));
    //        root.style.setProperty('--colorTheme', getComputedStyle(root).getPropertyValue('--darkColorTheme'));
    //        root.style.setProperty('--colorThemeBg', getComputedStyle(root).getPropertyValue('--darkColorThemeBg'));
    //        root.style.setProperty('--colorThemeSecondary', getComputedStyle(root).getPropertyValue('--darkColorThemeSecondary'));
    //        root.style.setProperty('--colorHover', getComputedStyle(root).getPropertyValue('--darkColorHover'));
    //        root.style.setProperty('--colorActive', getComputedStyle(root).getPropertyValue('--darkColorActive'));
    //        root.style.setProperty('--colorBorder', getComputedStyle(root).getPropertyValue('--darkColorBorder'));
    //        root.style.setProperty('--colorWarning', getComputedStyle(root).getPropertyValue('--darkColorWarning'));
    //        root.style.setProperty('--colorDefMod', getComputedStyle(root).getPropertyValue('--darkColorDefMod'));
    //        root.style.setProperty('--colorAddMod', getComputedStyle(root).getPropertyValue('--darkColorAddMod'));
    //        root.style.setProperty('--colorAdjustMod', getComputedStyle(root).getPropertyValue('--darkColorAdjustMod'));
    //        root.style.setProperty('--colorDisabled', getComputedStyle(root).getPropertyValue('--darkColorDisabled'));
    //        root.style.setProperty('--hedBgColor', getComputedStyle(root).getPropertyValue('--darkHedBgColor'));
    //        root.style.setProperty('--hedCartColor', getComputedStyle(root).getPropertyValue('--darkHedCartColor'));
    //        root.style.setProperty('--btnColor', getComputedStyle(root).getPropertyValue('--darkBtnColor'));
    //        root.style.setProperty('--btnColorBg', getComputedStyle(root).getPropertyValue('--darkBtnColorBg'));
    //        root.style.setProperty('--btnColorHover', getComputedStyle(root).getPropertyValue('--darkBtnColorHover'));
    //        root.style.setProperty('--btnColorBgHover', getComputedStyle(root).getPropertyValue('--darkBtnColorBgHover'));
    //        root.style.setProperty('--btnColorActive', getComputedStyle(root).getPropertyValue('--darkBtnColorActive'));
    //        root.style.setProperty('--btnColorDisabled', getComputedStyle(root).getPropertyValue('--darkBtnColorDisabled'));
    //        root.style.setProperty('--btnColorBgActive', getComputedStyle(root).getPropertyValue('--darkBtnColorBgActive'));
    //        root.style.setProperty('--btnPadding', getComputedStyle(root).getPropertyValue('--darkBtnPadding'));
    //        root.style.setProperty('--btnBorder', getComputedStyle(root).getPropertyValue('--darkBtnBorder'));
    //        root.style.setProperty('--btnRadius', getComputedStyle(root).getPropertyValue('--darkBtnRadius'));
    //        root.style.setProperty('--btnIconColor', getComputedStyle(root).getPropertyValue('--darkBtnIconColor'));
    //        root.style.setProperty('--btnIconColorBg', getComputedStyle(root).getPropertyValue('--darkBtnIconColorBg'));
    //        root.style.setProperty('--btnLinkColor', getComputedStyle(root).getPropertyValue('--darkBtnLinkColor'));
    //        root.style.setProperty('--catColor', getComputedStyle(root).getPropertyValue('--darkCatColor'));
    //        root.style.setProperty('--catColorBg', getComputedStyle(root).getPropertyValue('--darkCatColorBg'));
    //        root.style.setProperty('--catColorHover', getComputedStyle(root).getPropertyValue('--darkCatColorHover'));
    //        root.style.setProperty('--catColorActive', getComputedStyle(root).getPropertyValue('--darkCatColorActive'));
    //        root.style.setProperty('--ordBgImage', getComputedStyle(root).getPropertyValue('--darkOrdBgImage'));
    //        root.style.setProperty('--ordBgColor', getComputedStyle(root).getPropertyValue('--darkOrdBgColor'));
    //        root.style.setProperty('--itmBorder', getComputedStyle(root).getPropertyValue('--darkItmBorder'));
    //        root.style.setProperty('--itmColorBG', getComputedStyle(root).getPropertyValue('--darkItmColorBG'));
    //        root.style.setProperty('--diBorder', getComputedStyle(root).getPropertyValue('--darkDiBorder'));
    //        root.style.setProperty('--diColorBg', getComputedStyle(root).getPropertyValue('--darkDiColorBg'));
    //        root.style.setProperty('--shadow', getComputedStyle(root).getPropertyValue('--darkShadow'));
    //        root.style.setProperty('--backgroundImgUrl', getComputedStyle(root).getPropertyValue('--darkBackgroundImgUrl'));
    //        root.style.setProperty('--colorPlaceholder', getComputedStyle(root).getPropertyValue('--darkPlaceHolder'));
    //        root.style.setProperty('--colorPlaceholderOnly', getComputedStyle(root).getPropertyValue('--darkColorPlaceholderOnly'));
    //        root.style.setProperty('--colorDatalistHover', getComputedStyle(root).getPropertyValue('--darkColorDatalistHover'));
    //        root.style.setProperty('--colorOrderDetailAltRow', getComputedStyle(root).getPropertyValue('--darkColorOrderDetailAltRow'));
    //        root.style.setProperty('--colorOrderDetailHeader', getComputedStyle(root).getPropertyValue('--darkColorOrderDetailHeader'));
    //        root.style.setProperty('--colorOrderDetailHeaderText', getComputedStyle(root).getPropertyValue('--darkColorOrderDetailHeaderText'));
    //    } else {
    //        root.style.setProperty('--colorText', temp.Theme.colorText);
    //        root.style.setProperty('--colorInputBg', temp.Theme.colorInputBg);
    //        root.style.setProperty('--colorFocusBg', temp.Theme.colorFocusBg);
    //        root.style.setProperty('--colorFocus', temp.Theme.colorFocus);
    //        root.style.setProperty('--colorBg', temp.Theme.colorBg);
    //        root.style.setProperty('--colorBgCard', temp.Theme.colorBgCard);
    //        root.style.setProperty('--colorBgRow', temp.Theme.colorBgRow);
    //        root.style.setProperty('--colorTheme', temp.Theme.colorTheme);
    //        root.style.setProperty('--colorThemeBg', temp.Theme.colorThemeBg);
    //        root.style.setProperty('--colorThemeSecondary', temp.Theme.colorThemeSecondary);
    //        root.style.setProperty('--colorHover', temp.Theme.colorHover);
    //        root.style.setProperty('--colorActive', temp.Theme.colorActive);
    //        root.style.setProperty('--colorBorder', temp.Theme.colorBorder);
    //        root.style.setProperty('--colorWarning', temp.Theme.colorWarning);
    //        root.style.setProperty('--colorDefMod', temp.Theme.colorDefMod);
    //        root.style.setProperty('--colorAddMod', temp.Theme.colorAddMod);
    //        root.style.setProperty('--colorAdjustMod', temp.Theme.colorAdjustMod);
    //        root.style.setProperty('--colorDisabled', temp.Theme.colorDisabled);
    //        root.style.setProperty('--hedBgColor', temp.Theme.hedBgColor);
    //        root.style.setProperty('--hedCartColor', temp.Theme.hedCartColor);
    //        root.style.setProperty('--btnColor', temp.Theme.btnColor);
    //        root.style.setProperty('--btnColorBg', temp.Theme.btnColorBg);
    //        root.style.setProperty('--btnColorHover', temp.Theme.btnColorHover);
    //        root.style.setProperty('--btnColorBgHover', temp.Theme.btnColorBgHover);
    //        root.style.setProperty('--btnColorActive', temp.Theme.btnColorActive);
    //        root.style.setProperty('--btnColorDisabled', temp.Theme.btnColorDisabled);
    //        root.style.setProperty('--btnColorBgActive', temp.Theme.btnColorBgActive);
    //        root.style.setProperty('--btnPadding', temp.Theme.btnPadding);
    //        root.style.setProperty('--btnBorder', temp.Theme.btnBorder);
    //        root.style.setProperty('--btnRadius', temp.Theme.btnRadius);
    //        root.style.setProperty('--btnIconColor', temp.Theme.btnIconColor);
    //        root.style.setProperty('--btnIconColorBg', temp.Theme.btnIconColorBg);
    //        root.style.setProperty('--btnLinkColor', temp.Theme.btnLinkColor);
    //        root.style.setProperty('--catColor', temp.Theme.catColor);
    //        root.style.setProperty('--catColorBg', temp.Theme.catColorBg);
    //        root.style.setProperty('--catColorHover', temp.Theme.catColorHover);
    //        root.style.setProperty('--catColorActive', temp.Theme.catColorActive);
    //        root.style.setProperty('--ordBgImage', temp.Theme.ordBgImage);
    //        root.style.setProperty('--ordBgColor', temp.Theme.ordBgColor);
    //        root.style.setProperty('--itmBorder', temp.Theme.itmBorder);
    //        root.style.setProperty('--itmColorBG', temp.Theme.itmColorBG);
    //        root.style.setProperty('--diBorder', temp.Theme.diBorder);
    //        root.style.setProperty('--diColorBg', temp.Theme.diColorBg);
    //        root.style.setProperty('--shadow', temp.Theme.shadow);
    //        root.style.setProperty('--backgroundImgUrl', temp.Theme.backgroundImgUrl);
    //        root.style.setProperty('--colorPlaceholder', temp.Theme.placeHolder);
    //        root.style.setProperty('--colorPlaceholderOnly', temp.Theme.colorPlaceholderOnly);
    //        root.style.setProperty('--colorDatalistHover', temp.Theme.colorDatalistHover);
    //        root.style.setProperty('--colorOrderDetailAltRow', temp.Theme.colorOrderDetailAltRow);
    //        root.style.setProperty('--colorOrderDetailHeader', temp.Theme.colorOrderDetailHeader);
    //        root.style.setProperty('--colorOrderDetailHeaderText', temp.Theme.colorOrderDetailHeaderText);
    //    }
    //}

    //public static setDarkMode(user: User, localaOLO: IaOLO) {
    //    const darkModeEnabled = localStorage.getItem('darkMode') === 'true';
    //    if (darkModeEnabled)
    //        localaOLO.Temp.DarkMode = darkModeEnabled;

    //    if (localaOLO.Temp.DarkMode) {
    //        localaOLO.Temp.DarkMode = !localaOLO.Temp.DarkMode;
    //        this.toggleDarkMode(localaOLO.Temp, user);
    //    }
    //}

    //private static GetTheme(): ITempTheme {
    //    const root = document.documentElement;
    //    const theme: ITempTheme = {
    //        colorText: getComputedStyle(root).getPropertyValue('--colorText'),
    //        colorInputBg: getComputedStyle(root).getPropertyValue('--colorInputBg'),
    //        colorFocusBg: getComputedStyle(root).getPropertyValue('--colorFocusBg'),
    //        colorFocus: getComputedStyle(root).getPropertyValue('--colorFocus'),
    //        colorBg: getComputedStyle(root).getPropertyValue('--colorBg'),
    //        colorBgCard: getComputedStyle(root).getPropertyValue('--colorBgCard'),
    //        colorBgRow: getComputedStyle(root).getPropertyValue('--colorBgRow'),
    //        colorTheme: getComputedStyle(root).getPropertyValue('--colorTheme'),
    //        colorThemeBg: getComputedStyle(root).getPropertyValue('--colorThemeBg'),
    //        colorThemeSecondary: getComputedStyle(root).getPropertyValue('--colorThemeSecondary'),
    //        colorHover: getComputedStyle(root).getPropertyValue('--colorHover'),
    //        colorActive: getComputedStyle(root).getPropertyValue('--colorActive'),
    //        colorBorder: getComputedStyle(root).getPropertyValue('--colorBorder'),
    //        colorWarning: getComputedStyle(root).getPropertyValue('--colorWarning'),
    //        colorDefMod: getComputedStyle(root).getPropertyValue('--colorDefMod'),
    //        colorAddMod: getComputedStyle(root).getPropertyValue('--colorAddMod'),
    //        colorAdjustMod: getComputedStyle(root).getPropertyValue('--colorAdjustMod'),
    //        colorDisabled: getComputedStyle(root).getPropertyValue('--colorDisabled'),
    //        hedBgColor: getComputedStyle(root).getPropertyValue('--hedBgColor'),
    //        hedCartColor: getComputedStyle(root).getPropertyValue('--hedCartColor'),
    //        btnColor: getComputedStyle(root).getPropertyValue('--btnColor'),
    //        btnColorBg: getComputedStyle(root).getPropertyValue('--btnColorBg'),
    //        btnColorHover: getComputedStyle(root).getPropertyValue('--btnColorHover'),
    //        btnColorBgHover: getComputedStyle(root).getPropertyValue('--btnColorBgHover'),
    //        btnColorActive: getComputedStyle(root).getPropertyValue('--btnColorActive'),
    //        btnColorDisabled: getComputedStyle(root).getPropertyValue('--btnColorDisabled'),
    //        btnColorBgActive: getComputedStyle(root).getPropertyValue('--btnColorBgActive'),
    //        btnPadding: getComputedStyle(root).getPropertyValue('--btnPadding'),
    //        btnBorder: getComputedStyle(root).getPropertyValue('--btnBorder'),
    //        btnRadius: getComputedStyle(root).getPropertyValue('--btnRadius'),
    //        btnIconColor: getComputedStyle(root).getPropertyValue('--btnIconColor'),
    //        btnIconColorBg: getComputedStyle(root).getPropertyValue('--btnIconColorBg'),
    //        btnLinkColor: getComputedStyle(root).getPropertyValue('--btnLinkColor'),
    //        catColor: getComputedStyle(root).getPropertyValue('--catColor'),
    //        catColorBg: getComputedStyle(root).getPropertyValue('--catColorBg'),
    //        catColorHover: getComputedStyle(root).getPropertyValue('--catColorHover'),
    //        catColorActive: getComputedStyle(root).getPropertyValue('--catColorActive'),
    //        ordBgImage: getComputedStyle(root).getPropertyValue('--ordBgImage'),
    //        ordBgColor: getComputedStyle(root).getPropertyValue('--ordBgColor'),
    //        itmBorder: getComputedStyle(root).getPropertyValue('--itmBorder'),
    //        itmColorBG: getComputedStyle(root).getPropertyValue('--itmColorBG'),
    //        diBorder: getComputedStyle(root).getPropertyValue('--diBorder'),
    //        diColorBg: getComputedStyle(root).getPropertyValue('--diColorBg'),
    //        shadow: getComputedStyle(root).getPropertyValue('--shadow'),
    //        backgroundImgUrl: getComputedStyle(root).getPropertyValue('--backgroundImgUrl'),
    //        placeHolder: getComputedStyle(root).getPropertyValue('--colorPlaceholder'),
    //        colorPlaceholderOnly: getComputedStyle(root).getPropertyValue('--colorPlaceholderOnly'),
    //        colorDatalistHover: getComputedStyle(root).getPropertyValue('--colorDatalistHover'),
    //        colorOrderDetailAltRow: getComputedStyle(root).getPropertyValue('--colorOrderDetailAltRow'),
    //        colorOrderDetailHeader: getComputedStyle(root).getPropertyValue('--colorOrderDetailHeader'),
    //        colorOrderDetailHeaderText: getComputedStyle(root).getPropertyValue('--colorOrderDetailHeaderText')
    //    };
    //    return theme;
    //}

    ///**
    //* Initializes the aOLO.Temp object with various temporary properties.
    //* @returns {void}
    //*/
    //public static InitiateTemps(_localaOLO: IaOLO) {
    //    const siTemp = sessionStorage.getItem("siTemp");
    //    _localaOLO.Temp = {
    //        DarkMode: (siTemp && siTemp !== "") ? JSON.parse(siTemp).DarkMode : false,
    //        // languageCode: this.GetLocalCulture(),
    //        loyaltyPointCoupons: [],
    //        Mobile_Card: false,
    //        Theme: this.GetTheme(),
    //    };
    //}
}